import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../App';
import { 
  Building2, TrendingUp, TrendingDown, Search, Filter, 
  ChevronRight, ChevronDown, Loader2, Sparkles, RefreshCw, ArrowUpDown,
  IndianRupee, PieChart, BarChart3, AlertTriangle, Target, Eye,
  Printer, Download, FileSpreadsheet, ExternalLink, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import * as XLSX from 'xlsx';
// Extracted modal components for better maintainability
import { 
  HoldingsDetailModal, 
  NotesModal, 
  StockAnalysisModal,
  mapQualityName,
  getQualityColor,
  formatValue as formatValueUtil,
  getTabDisplayName as getTabDisplayNameUtil
} from '../components/companies';
import AddTransactionDialog from '../components/AddTransactionDialog';
import AllHoldingsView from '../components/AllHoldingsView';

const Companies = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [mutualFunds, setMutualFunds] = useState([]);
  const [unlistedStocks, setUnlistedStocks] = useState([]);
  const [mergedStocks, setMergedStocks] = useState([]);
  const [privateStocks, setPrivateStocks] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuality, setSelectedQuality] = useState('all');
  const [selectedMember, setSelectedMember] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'total_value', direction: 'desc' });
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'stocks'); // 'stocks', 'mutualfunds', 'unlisted', 'merged', 'private'
  const [restoringStock, setRestoringStock] = useState(null);
  const [tickerInputs, setTickerInputs] = useState({}); // Track ticker inputs for each stock
  const [priceInputs, setPriceInputs] = useState({}); // Track manual price inputs
  const [savingPrice, setSavingPrice] = useState(null);
  
  // Edit Mode state
  const [editMode, setEditMode] = useState(false);
  const [savingAction, setSavingAction] = useState(null);
  const [expandedEditRow, setExpandedEditRow] = useState(null); // Track which row has expanded edit form
  const [editFormData, setEditFormData] = useState({}); // {displayName: {action, newCompany, newTicker, swapRatio, price, notes}}
  const [stockNotes, setStockNotes] = useState({}); // Track notes for each stock
  
  // Stock analysis state
  const [selectedStock, setSelectedStock] = useState(null);
  const [stockAnalysis, setStockAnalysis] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  
  // Notes viewer state
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [viewingNotesStock, setViewingNotesStock] = useState(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editingNotesText, setEditingNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Data import state
  const [importing, setImporting] = useState(false);
  
  // All Holdings tab state (uses AllHoldingsView component for filters/sort/edit)
  const [allHoldings, setAllHoldings] = useState([]);
  const [members, setMembers] = useState([]);
  
  // Holdings detail modal state
  const [selectedCompanyForHoldings, setSelectedCompanyForHoldings] = useState(null);
  const [holdingsDetails, setHoldingsDetails] = useState([]);
  const [loadingHoldings, setLoadingHoldings] = useState(false);
  const [showHoldingsModal, setShowHoldingsModal] = useState(false);
  
  // Transaction dialog state
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [transactionSymbol, setTransactionSymbol] = useState('');
  const [highlightedSymbol, setHighlightedSymbol] = useState(null); // For highlighting row after dialog closes
  
  // Mobile card expansion state
  const [expandedCards, setExpandedCards] = useState(new Set());
  
  // Pledge status data
  const [pledgeData, setPledgeData] = useState({ las: [], mtf: [], free: [] });
  
  // Summary data
  const [totalValue, setTotalValue] = useState(0);
  const [stocksValue, setStocksValue] = useState(0);
  const [mfValue, setMfValue] = useState(0);
  const [uniqueQualityTags, setUniqueQualityTags] = useState([]);
  const [uniqueMembers, setUniqueMembers] = useState([]);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const [res, unlistedRes, mergedRes, privateRes, pledgeRes, holdingsRes, membersRes] = await Promise.all([
        api.get('/stocks/companies'),
        api.get('/symbols/unlisted').catch(() => ({ data: { unlisted_stocks: [] } })),
        api.get('/symbols/merged').catch(() => ({ data: { merged_stocks: [] } })),
        api.get('/symbols/private').catch(() => ({ data: { private_stocks: [] } })),
        api.get('/holdings/pledge-status').catch(() => ({ data: { las: { stocks: [] }, mtf: { stocks: [] }, free: { stocks: [] } } })),
        api.get('/holdings').catch(() => ({ data: [] })),
        api.get('/family-members').catch(() => ({ data: [] }))
      ]);
      const data = res.data;
      
      setCompanies(data.companies || []);
      setStocks(data.stocks || []);
      setMutualFunds(data.mutual_funds || []);
      setUnlistedStocks(unlistedRes.data?.unlisted_stocks || []);
      setMergedStocks(mergedRes.data?.merged_stocks || []);
      setPrivateStocks(privateRes.data?.private_stocks || []);
      setPledgeData(pledgeRes.data || { las: { stocks: [] }, mtf: { stocks: [] }, free: { stocks: [] } });
      setAllHoldings(holdingsRes.data || []);
      setMembers(membersRes.data || []);
      setTotalValue(data.total_value || 0);
      setStocksValue(data.stocks_value || 0);
      setMfValue(data.mf_value || 0);
      
      // Set filtered based on active tab
      if (activeTab === 'unlisted') {
        setFilteredCompanies(unlistedRes.data?.unlisted_stocks || []);
      } else if (activeTab === 'merged') {
        setFilteredCompanies(mergedRes.data?.merged_stocks || []);
      } else if (activeTab === 'private') {
        setFilteredCompanies(privateRes.data?.private_stocks || []);
      } else {
        setFilteredCompanies(activeTab === 'stocks' ? (data.stocks || []) : (data.mutual_funds || []));
      }
      
      // Extract unique quality tags and members (holders)
      const allTags = new Set();
      const allMembers = new Set();
      // Use stocks and mutual_funds data which have holders field
      [...(data.stocks || []), ...(data.mutual_funds || [])].forEach(c => {
        (c.quality_tags || []).forEach(t => allTags.add(t));
        (c.holders || []).forEach(m => allMembers.add(m));
      });
      setUniqueQualityTags(['all', ...Array.from(allTags).sort()]);
      setUniqueMembers(['all', ...Array.from(allMembers).sort()]);
      
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  // Scroll to highlighted symbol when it changes (after dialog closes)
  useEffect(() => {
    if (highlightedSymbol && !showTransactionDialog) {
      const element = document.getElementById(`stock-row-${highlightedSymbol}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightedSymbol, showTransactionDialog]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Handle tab change
  useEffect(() => {
    // Reset filters when switching tabs
    setSearchTerm('');
    setSelectedQuality('all');
    setSelectedMember('all');
    
    // Set the base data for filtering
    let baseData;
    if (activeTab === 'stocks') {
      baseData = stocks;
    } else if (activeTab === 'mutualfunds') {
      baseData = mutualFunds;
    } else if (activeTab === 'merged') {
      baseData = mergedStocks;
    } else if (activeTab === 'private') {
      baseData = privateStocks;
    } else {
      baseData = unlistedStocks;
    }
    setFilteredCompanies(baseData);
  }, [activeTab, stocks, mutualFunds, unlistedStocks, mergedStocks, privateStocks]);

  // Filter and sort companies
  useEffect(() => {
    // Get base data based on active tab
    let filtered;
    if (activeTab === 'stocks') {
      filtered = [...stocks];
    } else if (activeTab === 'mutualfunds') {
      filtered = [...mutualFunds];
    } else if (activeTab === 'merged') {
      filtered = [...mergedStocks];
    } else if (activeTab === 'private') {
      filtered = [...privateStocks];
    } else {
      filtered = [...unlistedStocks];
    }
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(c => {
        const symbol = c.symbol || c.display_name || '';
        const displayName = c.display_name || c.symbol || '';
        const newCompany = c.new_company_name || '';
        return symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
          displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          newCompany.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    
    // Quality filter (only for stocks/MFs, not unlisted/merged/private)
    if (selectedQuality !== 'all' && !['unlisted', 'merged', 'private'].includes(activeTab)) {
      filtered = filtered.filter(c => c.quality_tags?.includes(selectedQuality));
    }
    
    // Member filter
    if (selectedMember !== 'all') {
      filtered = filtered.filter(c => c.holders?.includes(selectedMember));
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle special cases
      if (sortConfig.key === 'total_value' && activeTab === 'unlisted') {
        aVal = a.total_cost || 0;
        bVal = b.total_cost || 0;
      } else if (sortConfig.key === 'quality_tag') {
        aVal = a.quality_tags?.[0] || 'zzz';
        bVal = b.quality_tags?.[0] || 'zzz';
      } else if (sortConfig.key === 'status') {
        aVal = a.pledged_quantity || 0;
        bVal = b.pledged_quantity || 0;
      }
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    setFilteredCompanies(filtered);
  }, [activeTab, stocks, mutualFunds, unlistedStocks, searchTerm, selectedQuality, selectedMember, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Toggle mobile card expansion
  const toggleCardExpand = (id) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleStockClick = async (symbol) => {
    setSelectedStock(symbol);
    setShowAnalysisModal(true);
    setStockAnalysis(null);
    setAiAnalysis(null);
    setLoadingAnalysis(true);
    
    try {
      const res = await api.get(`/stocks/analysis/${encodeURIComponent(symbol)}`);
      setStockAnalysis(res.data);
    } catch (error) {
      console.error('Error fetching stock analysis:', error);
      toast.error('Failed to load stock analysis');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleGetAIAnalysis = async () => {
    if (!selectedStock) return;
    
    setLoadingAI(true);
    try {
      const res = await api.post(`/stocks/ai-analysis/${encodeURIComponent(selectedStock)}`);
      setAiAnalysis(res.data.ai_analysis);
      toast.success('AI analysis generated successfully');
    } catch (error) {
      console.error('Error getting AI analysis:', error);
      toast.error('Failed to generate AI analysis');
    } finally {
      setLoadingAI(false);
    }
  };

  // Fetch holdings details for a specific symbol
  const handleViewHoldings = async (company) => {
    setSelectedCompanyForHoldings(company);
    setShowHoldingsModal(true);
    setLoadingHoldings(true);
    setHoldingsDetails([]);
    
    try {
      const res = await api.get(`/holdings/by-symbol/${encodeURIComponent(company.symbol)}`);
      setHoldingsDetails(res.data.holdings || []);
    } catch (error) {
      console.error('Error fetching holdings details:', error);
      toast.error('Failed to load holdings details');
    } finally {
      setLoadingHoldings(false);
    }
  };

  const formatValue = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  // Print unlisted stocks
  const handlePrintUnlisted = () => {
    const printContent = `
      <html>
        <head>
          <title>Unlisted Stocks Report - Maa Dabbu</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #dc2626; margin-bottom: 5px; }
            .subtitle { color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background-color: #fef2f2; color: #dc2626; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9fafb; }
            .total-row { background-color: #fef2f2; font-weight: bold; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .badge { padding: 2px 8px; border-radius: 12px; font-size: 12px; }
            .badge-delisted { background-color: #fee2e2; color: #dc2626; }
            .badge-merged { background-color: #fef3c7; color: #ca8a04; }
            .badge-other { background-color: #f3f4f6; color: #374151; }
            .new-company { color: #059669; font-weight: 500; }
            .swap-ratio { color: #2563eb; font-size: 11px; }
            .status-listed { color: #059669; }
            .status-unlisted { color: #6b7280; }
            .footer { margin-top: 30px; font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <h1>⚠️ Unlisted Stocks Report</h1>
          <p class="subtitle">Generated on ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          
          <table>
            <thead>
              <tr>
                <th>Company Name</th>
                <th class="text-right">Quantity</th>
                <th class="text-right">Cost Basis</th>
                <th class="text-center">Status</th>
                <th>New Company (Swap)</th>
                <th>Members</th>
              </tr>
            </thead>
            <tbody>
              ${unlistedStocks.map(stock => `
                <tr>
                  <td>
                    <strong>${stock.display_name}</strong><br>
                    <small>Marked: ${stock.marked_at ? new Date(stock.marked_at).toLocaleDateString() : 'Unknown'}</small>
                  </td>
                  <td class="text-right">${(stock.total_quantity || 0).toLocaleString()}</td>
                  <td class="text-right">₹${(stock.total_cost || 0).toLocaleString('en-IN')}</td>
                  <td class="text-center">
                    <span class="badge badge-${stock.reason === 'delisted' ? 'delisted' : stock.reason === 'merged' ? 'merged' : 'other'}">
                      ${stock.reason?.charAt(0).toUpperCase() + stock.reason?.slice(1) || 'Unlisted'}
                    </span>
                  </td>
                  <td>
                    ${stock.new_company ? `
                      <span class="new-company">${stock.new_company}</span><br>
                      ${stock.swap_ratio ? `<span class="swap-ratio">Ratio: ${stock.swap_ratio}</span><br>` : ''}
                      ${stock.new_company_status ? `<span class="${stock.new_company_status.toLowerCase().includes('listed') && !stock.new_company_status.toLowerCase().includes('unlisted') ? 'status-listed' : 'status-unlisted'}">${stock.new_company_status}</span>` : ''}
                    ` : '-'}
                  </td>
                  <td>${stock.members?.join(', ') || '-'}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td><strong>TOTAL</strong></td>
                <td class="text-right"><strong>${unlistedStocks.reduce((s, c) => s + (c.total_quantity || 0), 0).toLocaleString()}</strong></td>
                <td class="text-right"><strong>₹${unlistedStocks.reduce((s, c) => s + (c.total_cost || 0), 0).toLocaleString('en-IN')}</strong></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          
          <p class="footer">
            Total Unlisted Stocks: ${unlistedStocks.length} | 
            Total Cost Basis: ₹${unlistedStocks.reduce((s, c) => s + (c.total_cost || 0), 0).toLocaleString('en-IN')}
          </p>
        </body>
      </html>
    `;
    
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };
    }
  };

  // Export unlisted stocks to CSV
  const handleExportUnlisted = () => {
    if (unlistedStocks.length === 0) {
      toast.error('No unlisted stocks to export');
      return;
    }

    // Prepare CSV headers
    const headers = ['Company Name', 'Quantity', 'Cost Basis', 'Status', 'New Company', 'Swap Ratio', 'New Company Status', 'Members', 'Marked Date'];
    
    // Prepare CSV rows
    const rows = unlistedStocks.map(stock => [
      stock.display_name || '',
      stock.total_quantity || 0,
      stock.total_cost || 0,
      stock.reason || 'unlisted',
      stock.new_company || '',
      stock.swap_ratio || '',
      stock.new_company_status || '',
      stock.members?.join('; ') || '',
      stock.marked_at ? new Date(stock.marked_at).toLocaleDateString() : ''
    ]);

    // Add total row
    rows.push([
      'TOTAL',
      unlistedStocks.reduce((s, c) => s + (c.total_quantity || 0), 0),
      unlistedStocks.reduce((s, c) => s + (c.total_cost || 0), 0),
      '',
      '',
      '',
      '',
      '',
      ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => {
        // Escape commas and quotes in cell values
        const cellStr = String(cell);
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `unlisted_stocks_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${unlistedStocks.length} unlisted stocks to CSV`);
  };

  // Restore unlisted stock back to listed
  const handleRestoreToListed = async (displayName) => {
    setRestoringStock(displayName);
    try {
      const tickerSymbol = tickerInputs[displayName] || '';
      const response = await api.post('/symbols/restore-to-listed', {
        display_name: displayName,
        ticker_symbol: tickerSymbol
      });
      toast.success(response.data.message);
      // Clear the ticker input
      setTickerInputs(prev => {
        const newInputs = { ...prev };
        delete newInputs[displayName];
        return newInputs;
      });
      // Refresh the unlisted stocks list
      const unlistedResponse = await api.get('/symbols/unlisted');
      setUnlistedStocks(unlistedResponse.data.unlisted_stocks || []);
    } catch (error) {
      console.error('Error restoring stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore stock');
    } finally {
      setRestoringStock(null);
    }
  };

  // Handle ticker input change
  const handleTickerInputChange = (displayName, value) => {
    setTickerInputs(prev => ({
      ...prev,
      [displayName]: value.toUpperCase()
    }));
  };

  // Handle manual price input change
  const handlePriceInputChange = (displayName, value) => {
    setPriceInputs(prev => ({
      ...prev,
      [displayName]: value
    }));
  };

  // Save manual price for unlisted stock
  const handleSavePrice = async (displayName) => {
    const priceValue = priceInputs[displayName];
    if (!priceValue || isNaN(parseFloat(priceValue)) || parseFloat(priceValue) < 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setSavingPrice(displayName);
    try {
      const res = await api.post('/symbols/set-unlisted-price', {
        display_name: displayName,
        current_price: parseFloat(priceValue)
      });
      toast.success(res.data?.message || `Price set to ₹${parseFloat(priceValue).toLocaleString()} for ${displayName}`);
      
      // Update the local state to reflect the new price
      setUnlistedStocks(prev => prev.map(stock => 
        stock.display_name === displayName 
          ? { ...stock, manual_price: parseFloat(priceValue), has_manual_price: true }
          : stock
      ));
      
      // Clear the input
      setPriceInputs(prev => {
        const updated = { ...prev };
        delete updated[displayName];
        return updated;
      });
      
    } catch (error) {
      console.error('Error saving price:', error);
      toast.error(error.response?.data?.detail || 'Failed to save price');
    } finally {
      setSavingPrice(null);
    }
  };

  // Save notes for any stock
  const saveNotes = async (displayName, notes) => {
    try {
      await api.post('/symbols/save-notes', {
        display_name: displayName,
        notes: notes
      });
      toast.success(`Notes saved for ${displayName}`);
      // Update local state
      setStockNotes(prev => ({ ...prev, [displayName]: notes }));
      // Also update the stocks/companies arrays
      setStocks(prev => prev.map(s => 
        (s.symbol === displayName || s.display_name === displayName) 
          ? { ...s, notes } : s
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save notes');
    }
  };

  // Open transaction dialog with pre-filled symbol
  const openTransactionDialog = (symbol) => {
    setTransactionSymbol(symbol);
    setShowTransactionDialog(true);
  };

  // Handle edit form data change
  const handleEditFormChange = (displayName, field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [displayName]: {
        ...(prev[displayName] || {}),
        [field]: value
      }
    }));
  };

  // Mark stock as merged
  const markAsMerged = async (displayName) => {
    const data = editFormData[displayName] || {};
    if (!data.newCompany) {
      toast.error('Please enter the new company name');
      return;
    }

    setSavingAction(displayName);
    try {
      await api.post('/symbols/mark-merged', {
        display_name: displayName,
        new_company_name: data.newCompany,
        new_ticker: data.newTicker || null,
        swap_ratio: data.swapRatio || null,
        merger_notes: data.notes || null
      });
      
      toast.success(`${displayName} marked as merged with ${data.newCompany}`);
      setEditFormData(prev => { const u = {...prev}; delete u[displayName]; return u; });
      setExpandedEditRow(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as merged');
    } finally {
      setSavingAction(null);
    }
  };

  // Mark stock as private
  const markAsPrivate = async (displayName) => {
    const data = editFormData[displayName] || {};
    
    setSavingAction(displayName);
    try {
      await api.post('/symbols/mark-private', {
        display_name: displayName,
        manual_price: data.price ? parseFloat(data.price) : null,
        notes: data.notes || null
      });
      
      toast.success(`${displayName} marked as private${data.price ? ` with price ₹${data.price}` : ''}`);
      setEditFormData(prev => { const u = {...prev}; delete u[displayName]; return u; });
      setExpandedEditRow(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as private');
    } finally {
      setSavingAction(null);
    }
  };

  // Mark stock as unlisted
  const markAsUnlisted = async (displayName) => {
    setSavingAction(displayName);
    try {
      await api.post('/symbols/mark-unlisted', {
        display_names: [displayName],
        reason: 'unlisted'
      });
      toast.success(`${displayName} marked as unlisted`);
      setExpandedEditRow(null);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark as unlisted');
    } finally {
      setSavingAction(null);
    }
  };

  // Fix ticker mapping
  const fixTickerMapping = async (displayName) => {
    const newTicker = tickerInputs[displayName];
    if (!newTicker) {
      toast.error('Please enter a ticker symbol');
      return;
    }
    
    setSavingAction(displayName);
    try {
      await api.post('/symbols/confirm-mapping', {
        display_name: displayName,
        ticker_symbol: newTicker.toUpperCase()
      });
      toast.success(`Ticker updated to ${newTicker.toUpperCase()} for ${displayName}`);
      setTickerInputs(prev => { const u = {...prev}; delete u[displayName]; return u; });
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update ticker');
    } finally {
      setSavingAction(null);
    }
  };

  // Quality name mapping from Excel file names to display names
  const mapQualityName = (name) => {
    if (!name) return 'Unknown';
    const QUALITY_MAP = {
      'NStock-Low': 'LOW',
      'NStock- V Low': 'V LOW',
      'NStock- VV LOW': 'VV LOW',
      'NStock-Moderate': 'MODERATE',
      'NStock-High': 'HIGH',
      'NStock-New': 'NEW',
      'NStock-V Low': 'V LOW',
      'NStock-VV Low': 'VV LOW',
    };
    if (QUALITY_MAP[name]) return QUALITY_MAP[name];
    const lowerName = name.toLowerCase();
    if (lowerName.includes('vv low')) return 'VV LOW';
    if (lowerName.includes('v low')) return 'V LOW';
    if (lowerName.includes('low')) return 'LOW';
    if (lowerName.includes('moderate')) return 'MODERATE';
    if (lowerName.includes('high')) return 'HIGH';
    if (lowerName.includes('new')) return 'NEW';
    return name;
  };

  const getQualityColor = (tag) => {
    const mappedTag = mapQualityName(tag);
    const colors = {
      'NEW': 'bg-blue-100 text-blue-800',
      'HIGH': 'bg-green-100 text-green-800',
      'MODERATE': 'bg-yellow-100 text-yellow-800',
      'LOW': 'bg-orange-100 text-orange-800',
      'V LOW': 'bg-red-100 text-red-800',
      'VV LOW': 'bg-red-200 text-red-900',
    };
    return colors[mappedTag] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={48} />
        </div>
      </Layout>
    );
  }

  // Function to import seed data
  const importSeedData = async () => {
    setImporting(true);
    try {
      // Use force=true to ensure data gets updated
      const response = await api.post('/symbols/seed-data?force=true');
      const data = response.data;
      
      if (data.imported > 0 || data.updated > 0) {
        toast.success(`Import complete! ${data.imported} new, ${data.updated} updated`);
        // Show final counts
        if (data.final_counts) {
          toast.info(`Final: ${data.final_counts.unlisted} unlisted, ${data.final_counts.merged} merged, ${data.final_counts.private} private`);
        }
        fetchCompanies(); // Refresh the data
      } else {
        toast.info(data.message || 'No changes needed');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // Get tab display name
  const getTabDisplayName = () => {
    switch (activeTab) {
      case 'stocks': return 'Stocks';
      case 'mutualfunds': return 'Mutual Funds';
      case 'pledge': return 'Pledge Status';
      case 'merged': return 'Merged Stocks';
      case 'private': return 'Private Stocks';
      case 'unlisted': return 'Unlisted Stocks';
      default: return 'Data';
    }
  };

  // Export to Excel handler
  const handleExportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();
      const timestamp = new Date().toISOString().split('T')[0];
      
      if (activeTab === 'pledge') {
        // Pledge Status: Master sheet + 3 separate sheets
        const masterData = [];
        
        // LAS data by bank
        (pledgeData.las?.banks || []).forEach(bank => {
          const bankStocks = pledgeData.las?.by_bank?.[bank]?.stocks || [];
          bankStocks.forEach(stock => {
            masterData.push({
              'Category': 'LAS',
              'Bank': bank,
              'Symbol': stock.symbol,
              'Display Name': stock.display_name || stock.symbol,
              'Pledged Qty': stock.quantity,
              'Current Price': stock.current_price,
              'Current Value': stock.current_value,
              'Total Cost': stock.total_cost,
              'Gain': stock.gain,
              'Gain %': stock.gain_percent,
              'Members': (stock.members || []).join(', ')
            });
          });
        });
        
        // MTF data
        (pledgeData.mtf?.stocks || []).forEach(stock => {
          masterData.push({
            'Category': 'MTF',
            'Bank': 'MTF',
            'Symbol': stock.symbol,
            'Display Name': stock.display_name || stock.symbol,
            'Pledged Qty': stock.quantity,
            'Current Price': stock.current_price,
            'Current Value': stock.current_value,
            'Total Cost': stock.total_cost,
            'Gain': stock.gain,
            'Gain %': stock.gain_percent,
            'Members': (stock.members || []).join(', ')
          });
        });
        
        // Free data
        (pledgeData.free?.stocks || []).forEach(stock => {
          masterData.push({
            'Category': 'Free',
            'Bank': '-',
            'Symbol': stock.symbol,
            'Display Name': stock.display_name || stock.symbol,
            'Pledged Qty': stock.quantity,
            'Current Price': stock.current_price,
            'Current Value': stock.current_value,
            'Total Cost': stock.total_cost,
            'Gain': stock.gain,
            'Gain %': stock.gain_percent,
            'Members': (stock.members || []).join(', ')
          });
        });
        
        // Master sheet
        const masterWs = XLSX.utils.json_to_sheet(masterData);
        XLSX.utils.book_append_sheet(wb, masterWs, 'All Pledge Status');
        
        // LAS sheet (combined all banks)
        const lasData = [];
        (pledgeData.las?.banks || []).forEach(bank => {
          const bankStocks = pledgeData.las?.by_bank?.[bank]?.stocks || [];
          bankStocks.forEach(stock => {
            lasData.push({
              'Bank': bank,
              'Symbol': stock.symbol,
              'Display Name': stock.display_name || stock.symbol,
              'Pledged Qty': stock.quantity,
              'Current Price': stock.current_price,
              'Current Value': stock.current_value,
              'Total Cost': stock.total_cost,
              'Gain': stock.gain,
              'Gain %': stock.gain_percent,
              'Members': (stock.members || []).join(', ')
            });
          });
        });
        if (lasData.length > 0) {
          const lasWs = XLSX.utils.json_to_sheet(lasData);
          XLSX.utils.book_append_sheet(wb, lasWs, 'LAS Pledged');
        }
        
        // MTF sheet
        const mtfData = (pledgeData.mtf?.stocks || []).map(stock => ({
          'Symbol': stock.symbol,
          'Display Name': stock.display_name || stock.symbol,
          'Qty': stock.quantity,
          'Current Price': stock.current_price,
          'Current Value': stock.current_value,
          'Total Cost': stock.total_cost,
          'Gain': stock.gain,
          'Gain %': stock.gain_percent,
          'Members': (stock.members || []).join(', ')
        }));
        const mtfWs = XLSX.utils.json_to_sheet(mtfData.length > 0 ? mtfData : [{ 'Note': 'No MTF holdings found' }]);
        XLSX.utils.book_append_sheet(wb, mtfWs, 'MTF');
        
        // Free sheet
        const freeData = (pledgeData.free?.stocks || []).map(stock => ({
          'Symbol': stock.symbol,
          'Display Name': stock.display_name || stock.symbol,
          'Qty': stock.quantity,
          'Current Price': stock.current_price,
          'Current Value': stock.current_value,
          'Total Cost': stock.total_cost,
          'Gain': stock.gain,
          'Gain %': stock.gain_percent,
          'Members': (stock.members || []).join(', ')
        }));
        if (freeData.length > 0) {
          const freeWs = XLSX.utils.json_to_sheet(freeData);
          XLSX.utils.book_append_sheet(wb, freeWs, 'Free Shares');
        }
        
        XLSX.writeFile(wb, `MaaDabbu_PledgeStatus_${timestamp}.xlsx`);
        toast.success('Pledge Status exported to Excel');
        
      } else {
        // Other tabs: Single sheet with filtered data
        let exportData = [];
        
        if (activeTab === 'stocks') {
          exportData = filteredCompanies.map(c => ({
            'Symbol': c.symbol,
            'Display Name': c.display_name || c.symbol,
            'Qty': c.total_quantity,
            'Avg Price': c.avg_price,
            'Current Price': c.current_price,
            'Value': c.total_value,
            'Invested': c.total_invested,
            'Gain': c.gain,
            'Gain %': c.gain_percent,
            'Quality': (c.quality_tags || []).join(', '),
            'Members': (c.members || []).join(', ')
          }));
        } else if (activeTab === 'mutualfunds') {
          exportData = filteredCompanies.map(c => ({
            'Symbol': c.symbol,
            'Display Name': c.display_name || c.symbol,
            'Units': c.total_quantity,
            'Avg NAV': c.avg_price,
            'Current NAV': c.current_price,
            'Value': c.total_value,
            'Invested': c.total_invested,
            'Gain': c.gain,
            'Gain %': c.gain_percent,
            'Members': (c.members || []).join(', ')
          }));
        } else if (activeTab === 'merged') {
          exportData = filteredCompanies.map(c => ({
            'Original Symbol': c.display_name,
            'New Company': c.new_company_name || '-',
            'Swap Ratio': c.swap_ratio || '-',
            'Qty': c.total_quantity,
            'Total Cost': c.total_cost,
            'Notes': c.notes || ''
          }));
        } else if (activeTab === 'private') {
          exportData = filteredCompanies.map(c => ({
            'Symbol': c.display_name,
            'Qty': c.total_quantity,
            'Manual Price': c.manual_price,
            'Current Value': c.total_current_value,
            'Total Cost': c.total_cost,
            'Notes': c.notes || ''
          }));
        } else if (activeTab === 'unlisted') {
          exportData = filteredCompanies.map(c => ({
            'Symbol': c.display_name,
            'Qty': c.total_quantity,
            'Avg Price': c.avg_price,
            'Total Cost': c.total_cost,
            'Notes': c.notes || ''
          }));
        }
        
        if (exportData.length === 0) {
          toast.error('No data to export');
          return;
        }
        
        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, getTabDisplayName());
        XLSX.writeFile(wb, `MaaDabbu_${getTabDisplayName().replace(/\s+/g, '_')}_${timestamp}.xlsx`);
        toast.success(`${getTabDisplayName()} exported to Excel (${exportData.length} records)`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export: ' + error.message);
    }
  };

  // Print handler
  const handlePrint = () => {
    const tabName = getTabDisplayName();
    let printContent = '';
    
    if (activeTab === 'pledge') {
      // Pledge Status print
      const lasSummary = pledgeData.las?.total_summary || {};
      const mtfSummary = pledgeData.mtf?.summary || {};
      const freeSummary = pledgeData.free?.summary || {};
      
      printContent = `
        <html>
          <head>
            <title>${tabName} - Maa Dabbu</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
              h1 { color: #1e3a5f; margin-bottom: 5px; font-size: 18px; }
              h2 { color: #333; margin: 15px 0 10px 0; font-size: 14px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
              .subtitle { color: #666; margin-bottom: 15px; font-size: 11px; }
              .summary-cards { display: flex; gap: 15px; margin-bottom: 20px; }
              .summary-card { padding: 10px; border-radius: 6px; flex: 1; }
              .las-card { background: #fef2f2; border: 1px solid #fecaca; }
              .mtf-card { background: #fffbeb; border: 1px solid #fde68a; }
              .free-card { background: #f0fdf4; border: 1px solid #bbf7d0; }
              .card-title { font-weight: bold; font-size: 11px; }
              .card-value { font-size: 16px; font-weight: bold; margin: 5px 0; }
              .card-subtitle { font-size: 10px; color: #666; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10px; }
              th { background: #f3f4f6; text-align: left; padding: 6px; border: 1px solid #e5e7eb; font-weight: 600; }
              td { padding: 6px; border: 1px solid #e5e7eb; }
              .text-right { text-align: right; }
              .text-green { color: #16a34a; }
              .text-red { color: #dc2626; }
              .bank-header { background: #fef2f2; padding: 8px; margin-top: 15px; font-weight: bold; border-radius: 4px; }
              @media print { body { padding: 10px; } }
            </style>
          </head>
          <body>
            <h1>📊 Pledge Status Report</h1>
            <div class="subtitle">Generated on ${new Date().toLocaleString()} | Maa Dabbu Portfolio</div>
            
            <div class="summary-cards">
              <div class="summary-card las-card">
                <div class="card-title">🔒 LAS Pledged</div>
                <div class="card-value">₹${(lasSummary.total_value || 0).toLocaleString('en-IN')}</div>
                <div class="card-subtitle">${(lasSummary.total_qty || 0).toLocaleString()} shares • ${pledgeData.las?.banks?.length || 0} banks</div>
              </div>
              <div class="summary-card mtf-card">
                <div class="card-title">📈 MTF Holdings</div>
                <div class="card-value">₹${(mtfSummary.total_value || 0).toLocaleString('en-IN')}</div>
                <div class="card-subtitle">${(mtfSummary.total_qty || 0).toLocaleString()} shares</div>
              </div>
              <div class="summary-card free-card">
                <div class="card-title">✅ Free (Unpledged)</div>
                <div class="card-value">₹${(freeSummary.total_value || 0).toLocaleString('en-IN')}</div>
                <div class="card-subtitle">${(freeSummary.total_qty || 0).toLocaleString()} shares</div>
              </div>
            </div>
            
            ${(pledgeData.las?.banks || []).map(bank => `
              <div class="bank-header">🏦 ${bank} LAS (${pledgeData.las?.by_bank?.[bank]?.stocks?.length || 0} stocks) - ₹${(pledgeData.las?.by_bank?.[bank]?.summary?.total_value || 0).toLocaleString('en-IN')}</div>
              <table>
                <thead>
                  <tr><th>Stock</th><th class="text-right">Pledged Qty</th><th class="text-right">Value</th><th class="text-right">Gain</th><th>Members</th></tr>
                </thead>
                <tbody>
                  ${(pledgeData.las?.by_bank?.[bank]?.stocks || []).map(stock => `
                    <tr>
                      <td>${stock.display_name || stock.symbol}</td>
                      <td class="text-right">${stock.quantity?.toLocaleString()}</td>
                      <td class="text-right">₹${(stock.current_value || 0).toLocaleString('en-IN')}</td>
                      <td class="text-right ${stock.gain >= 0 ? 'text-green' : 'text-red'}">₹${(stock.gain || 0).toLocaleString('en-IN')} (${stock.gain_percent || 0}%)</td>
                      <td>${(stock.members || []).join(', ')}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            `).join('')}
            
            <h2>✅ Free (Unpledged) Shares - Top 50</h2>
            <table>
              <thead>
                <tr><th>Stock</th><th class="text-right">Qty</th><th class="text-right">Value</th><th class="text-right">Gain</th><th>Members</th></tr>
              </thead>
              <tbody>
                ${(pledgeData.free?.stocks || []).slice(0, 50).map(stock => `
                  <tr>
                    <td>${stock.display_name || stock.symbol}</td>
                    <td class="text-right">${stock.quantity?.toLocaleString()}</td>
                    <td class="text-right">₹${(stock.current_value || 0).toLocaleString('en-IN')}</td>
                    <td class="text-right ${stock.gain >= 0 ? 'text-green' : 'text-red'}">₹${(stock.gain || 0).toLocaleString('en-IN')} (${stock.gain_percent || 0}%)</td>
                    <td>${(stock.members || []).join(', ')}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            ${(pledgeData.free?.stocks || []).length > 50 ? `<p style="color: #666; font-size: 10px;">Showing 50 of ${pledgeData.free.stocks.length} free stocks. Export to Excel for full list.</p>` : ''}
          </body>
        </html>
      `;
    } else {
      // Other tabs print
      const data = filteredCompanies;
      const totalValue = data.reduce((sum, c) => sum + (c.total_value || c.total_current_value || c.total_cost || 0), 0);
      const totalGain = data.reduce((sum, c) => sum + (c.gain || 0), 0);
      
      let tableHeaders = '';
      let tableRows = '';
      
      if (activeTab === 'stocks') {
        tableHeaders = '<th>Symbol</th><th class="text-right">Qty</th><th class="text-right">Price</th><th class="text-right">Value</th><th class="text-right">Gain %</th><th>Quality</th><th>Members</th>';
        tableRows = data.map(c => `
          <tr>
            <td>${c.display_name || c.symbol}</td>
            <td class="text-right">${c.total_quantity?.toLocaleString()}</td>
            <td class="text-right">₹${c.current_price?.toLocaleString('en-IN')}</td>
            <td class="text-right">₹${(c.total_value || 0).toLocaleString('en-IN')}</td>
            <td class="text-right ${c.gain_percent >= 0 ? 'text-green' : 'text-red'}">${c.gain_percent?.toFixed(1)}%</td>
            <td>${(c.quality_tags || []).join(', ')}</td>
            <td>${(c.members || []).join(', ')}</td>
          </tr>
        `).join('');
      } else if (activeTab === 'mutualfunds') {
        tableHeaders = '<th>Fund Name</th><th class="text-right">Units</th><th class="text-right">NAV</th><th class="text-right">Value</th><th class="text-right">Gain %</th><th>Members</th>';
        tableRows = data.map(c => `
          <tr>
            <td>${c.display_name || c.symbol}</td>
            <td class="text-right">${c.total_quantity?.toLocaleString()}</td>
            <td class="text-right">₹${c.current_price?.toLocaleString('en-IN')}</td>
            <td class="text-right">₹${(c.total_value || 0).toLocaleString('en-IN')}</td>
            <td class="text-right ${c.gain_percent >= 0 ? 'text-green' : 'text-red'}">${c.gain_percent?.toFixed(1)}%</td>
            <td>${(c.members || []).join(', ')}</td>
          </tr>
        `).join('');
      } else if (activeTab === 'merged') {
        tableHeaders = '<th>Original Stock</th><th>New Company</th><th>Swap Ratio</th><th class="text-right">Qty</th><th class="text-right">Cost</th><th>Notes</th>';
        tableRows = data.map(c => `
          <tr>
            <td>${c.display_name}</td>
            <td>${c.new_company_name || '-'}</td>
            <td>${c.swap_ratio || '-'}</td>
            <td class="text-right">${c.total_quantity?.toLocaleString()}</td>
            <td class="text-right">₹${(c.total_cost || 0).toLocaleString('en-IN')}</td>
            <td>${c.notes || '-'}</td>
          </tr>
        `).join('');
      } else if (activeTab === 'private') {
        tableHeaders = '<th>Symbol</th><th class="text-right">Qty</th><th class="text-right">Manual Price</th><th class="text-right">Value</th><th class="text-right">Cost</th><th>Notes</th>';
        tableRows = data.map(c => `
          <tr>
            <td>${c.display_name}</td>
            <td class="text-right">${c.total_quantity?.toLocaleString()}</td>
            <td class="text-right">₹${c.manual_price?.toLocaleString('en-IN')}</td>
            <td class="text-right">₹${(c.total_current_value || 0).toLocaleString('en-IN')}</td>
            <td class="text-right">₹${(c.total_cost || 0).toLocaleString('en-IN')}</td>
            <td>${c.notes || '-'}</td>
          </tr>
        `).join('');
      } else if (activeTab === 'unlisted') {
        tableHeaders = '<th>Symbol</th><th class="text-right">Qty</th><th class="text-right">Avg Price</th><th class="text-right">Total Cost</th><th>Notes</th>';
        tableRows = data.map(c => `
          <tr>
            <td>${c.display_name}</td>
            <td class="text-right">${c.total_quantity?.toLocaleString()}</td>
            <td class="text-right">₹${c.avg_price?.toLocaleString('en-IN')}</td>
            <td class="text-right">₹${(c.total_cost || 0).toLocaleString('en-IN')}</td>
            <td>${c.notes || '-'}</td>
          </tr>
        `).join('');
      }
      
      printContent = `
        <html>
          <head>
            <title>${tabName} - Maa Dabbu</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
              h1 { color: #1e3a5f; margin-bottom: 5px; font-size: 18px; }
              .subtitle { color: #666; margin-bottom: 15px; font-size: 11px; }
              .summary { display: flex; gap: 20px; margin-bottom: 15px; padding: 10px; background: #f9fafb; border-radius: 6px; }
              .summary-item { }
              .summary-label { font-size: 10px; color: #666; }
              .summary-value { font-size: 14px; font-weight: bold; }
              table { width: 100%; border-collapse: collapse; font-size: 10px; }
              th { background: #f3f4f6; text-align: left; padding: 6px; border: 1px solid #e5e7eb; font-weight: 600; }
              td { padding: 6px; border: 1px solid #e5e7eb; }
              .text-right { text-align: right; }
              .text-green { color: #16a34a; }
              .text-red { color: #dc2626; }
              @media print { body { padding: 10px; } }
            </style>
          </head>
          <body>
            <h1>📊 ${tabName} Report</h1>
            <div class="subtitle">Generated on ${new Date().toLocaleString()} | Maa Dabbu Portfolio | ${data.length} records</div>
            
            <div class="summary">
              <div class="summary-item">
                <div class="summary-label">Total Records</div>
                <div class="summary-value">${data.length}</div>
              </div>
              <div class="summary-item">
                <div class="summary-label">Total Value</div>
                <div class="summary-value">₹${totalValue.toLocaleString('en-IN')}</div>
              </div>
              ${activeTab === 'stocks' || activeTab === 'mutualfunds' ? `
              <div class="summary-item">
                <div class="summary-label">Total Gain</div>
                <div class="summary-value ${totalGain >= 0 ? 'text-green' : 'text-red'}">₹${totalGain.toLocaleString('en-IN')}</div>
              </div>
              ` : ''}
            </div>
            
            <table>
              <thead><tr>${tableHeaders}</tr></thead>
              <tbody>${tableRows}</tbody>
            </table>
          </body>
        </html>
      `;
    }
    
    const blob = new Blob([printContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };
    }
  };

  return (
    <Layout user={user}>
      <div className="flex flex-col h-[calc(100vh-10rem)] sm:h-[calc(100vh-10rem)] md:h-[calc(100vh-10rem)] lg:h-[calc(100vh-12rem)] overflow-hidden">
        {/* Header - Mobile Responsive */}
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-3">
          <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
            <Building2 className="text-primary flex-shrink-0" size={20} />
            <span className="hidden xs:inline">Know your Stock</span>
            <span className="xs:hidden">Stocks</span>
          </h1>
          {/* Action buttons - scrollable on mobile */}
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto pb-1 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
            <Button 
              onClick={importSeedData}
              variant="outline"
              size="sm"
              disabled={importing}
              className="flex items-center gap-1 text-xs bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700 whitespace-nowrap flex-shrink-0"
            >
              {importing ? '⏳' : '📥'} <span className="hidden sm:inline">{importing ? 'Importing...' : 'Import Data'}</span>
            </Button>
            <Button 
              onClick={fetchCompanies} 
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-xs whitespace-nowrap flex-shrink-0"
            >
              <RefreshCw size={14} /> <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              variant={editMode ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setEditMode(!editMode);
                setExpandedEditRow(null);
              }}
              className={`flex items-center gap-1 text-xs whitespace-nowrap flex-shrink-0 ${editMode ? "bg-blue-600 hover:bg-blue-700" : ""}`}
            >
              {editMode ? '✓' : '✏️'} <span className="hidden sm:inline">{editMode ? 'Exit Edit' : 'Edit Mode'}</span>
            </Button>
            <Button
              onClick={handlePrint}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-xs whitespace-nowrap flex-shrink-0"
              data-testid="print-button"
            >
              <Printer size={14} /> <span className="hidden sm:inline">Print</span>
            </Button>
            <Button
              onClick={handleExportExcel}
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-xs bg-green-50 hover:bg-green-100 border-green-200 text-green-700 whitespace-nowrap flex-shrink-0"
              data-testid="export-excel-button"
            >
              <FileSpreadsheet size={14} /> <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>

        {/* Tabs - Horizontally scrollable on mobile */}
        <div className="flex-shrink-0 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-1.5 border-b pb-1.5 min-w-max sm:min-w-0 sm:flex-wrap">
          <button
            onClick={() => setActiveTab('stocks')}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === 'stocks'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Stocks ({stocks.length})
          </button>
          <button
            onClick={() => setActiveTab('mutualfunds')}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === 'mutualfunds'
                ? 'bg-primary text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            MFs ({mutualFunds.length})
          </button>
          <button
            onClick={() => setActiveTab('pledge')}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === 'pledge'
                ? 'bg-purple-600 text-white'
                : 'bg-purple-50 text-purple-600 hover:bg-purple-100'
            }`}
          >
            📊 Pledge
          </button>
          {mergedStocks.length > 0 && (
            <button
              onClick={() => setActiveTab('merged')}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap ${
                activeTab === 'merged'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
            >
              Merged ({mergedStocks.length})
            </button>
          )}
          {privateStocks.length > 0 && (
            <button
              onClick={() => setActiveTab('private')}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap ${
                activeTab === 'private'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
              }`}
            >
              Private ({privateStocks.length})
            </button>
          )}
          {unlistedStocks.length > 0 && (
            <button
              onClick={() => setActiveTab('unlisted')}
              className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap ${
                activeTab === 'unlisted'
                  ? 'bg-amber-600 text-white'
                  : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
              }`}
            >
              Unlisted ({unlistedStocks.length})
            </button>
          )}
          <button
            onClick={() => setActiveTab('holdings')}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg transition-all whitespace-nowrap ${
              activeTab === 'holdings'
                ? 'bg-indigo-600 text-white'
                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
            }`}
          >
            📋 All Holdings ({allHoldings.length})
          </button>
          </div>
        </div>

        {/* Compact Stats Bar + Search/Filters - Combined (hide for Pledge and Holdings tabs) */}
        {activeTab !== 'pledge' && activeTab !== 'holdings' && (
        <div className="flex-shrink-0 card p-2 sm:p-2.5">
          {/* Stats Row - Scrollable on mobile */}
          <div className="flex items-center gap-2 sm:gap-3 mb-2 pb-2 border-b border-gray-100 overflow-x-auto -mx-2 px-2 sm:mx-0 sm:px-0">
            {/* Count */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
              <span className="text-xs text-gray-500 uppercase">
                {activeTab === 'stocks' ? 'Stocks' : activeTab === 'mutualfunds' ? 'MFs' : activeTab === 'merged' ? 'Merged' : activeTab === 'private' ? 'Private' : 'Unlisted'}:
              </span>
              <span className={`text-sm font-bold ${
                activeTab === 'unlisted' ? 'text-amber-600' : activeTab === 'merged' ? 'text-blue-600' : activeTab === 'private' ? 'text-emerald-600' : 'text-primary'
              }`}>
                {activeTab === 'stocks' ? stocks.length : activeTab === 'mutualfunds' ? mutualFunds.length : activeTab === 'merged' ? mergedStocks.length : activeTab === 'private' ? privateStocks.length : unlistedStocks.length}
              </span>
            </div>
            <span className="text-gray-300 flex-shrink-0">|</span>
            
            {/* Value */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
              <span className="text-xs text-gray-500 uppercase">
                {activeTab === 'unlisted' || activeTab === 'merged' ? 'Cost' : 'Value'}:
              </span>
              <span className={`text-sm font-bold ${
                activeTab === 'unlisted' ? 'text-orange-600' : activeTab === 'merged' ? 'text-indigo-600' : activeTab === 'private' ? 'text-teal-600' : 'text-blue-600'
              }`}>
                {activeTab === 'unlisted' 
                  ? formatValue(unlistedStocks.reduce((s, c) => s + (c.total_cost || 0), 0))
                  : activeTab === 'merged'
                  ? formatValue(mergedStocks.reduce((s, c) => s + (c.total_cost || 0), 0))
                  : activeTab === 'private'
                  ? formatValue(privateStocks.reduce((s, c) => s + (c.total_current_value || 0), 0))
                  : formatValue(activeTab === 'stocks' ? stocksValue : mfValue)
                }
              </span>
            </div>
            
            {/* Gain (only for stocks/MFs) */}
            {!['unlisted', 'merged', 'private'].includes(activeTab) && (
              <>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 uppercase">Gain:</span>
                  <span className={`text-sm font-bold ${
                    (activeTab === 'stocks' ? stocks : mutualFunds).reduce((s, c) => s + (c.gain || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatValue((activeTab === 'stocks' ? stocks : mutualFunds).reduce((s, c) => s + (c.gain || 0), 0))}
                  </span>
                </div>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500 uppercase">Avg:</span>
                  <span className={`text-sm font-bold ${
                    (activeTab === 'stocks' ? stocks : mutualFunds).length > 0 &&
                    (activeTab === 'stocks' ? stocks : mutualFunds).reduce((s, c) => s + (c.gain_percent || 0), 0) / 
                    (activeTab === 'stocks' ? stocks : mutualFunds).length >= 0 ? 'text-purple-600' : 'text-red-600'
                  }`}>
                    {(activeTab === 'stocks' ? stocks : mutualFunds).length > 0 
                      ? ((activeTab === 'stocks' ? stocks : mutualFunds).reduce((s, c) => s + (c.gain_percent || 0), 0) / 
                         (activeTab === 'stocks' ? stocks : mutualFunds).length).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </>
            )}
            
            {/* Actions for Unlisted */}
            {activeTab === 'unlisted' && (
              <>
                <span className="text-gray-300">|</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-500">Holdings:</span>
                  <span className="text-sm font-bold text-gray-700">
                    {unlistedStocks.reduce((s, c) => s + (c.holdings_count || 0), 0)}
                  </span>
                </div>
                <div className="ml-auto flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={handlePrintUnlisted} className="text-xs h-7 px-2" disabled={unlistedStocks.length === 0}>
                    <Printer size={12} className="mr-1" /> Print
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleExportUnlisted} className="text-xs h-7 px-2 bg-green-50 hover:bg-green-100 border-green-200" disabled={unlistedStocks.length === 0}>
                    <FileSpreadsheet size={12} className="mr-1" /> Export
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate('/imports?tab=symbols')} className="text-xs h-7 px-2 bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700">
                    <ExternalLink size={12} className="mr-1" /> Symbol Health
                  </Button>
                </div>
              </>
            )}
            
            {/* Edit Mode Indicator */}
            {editMode && (
              <span className="ml-auto text-xs text-blue-600 font-medium flex items-center gap-1">
                ✏️ Edit Mode Active
              </span>
            )}
          </div>
          
          {/* Search & Filters Row */}
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <div className="flex-1 min-w-0">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                  type="text"
                  placeholder={activeTab === 'stocks' ? "Search symbol or name..." : "Search..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field w-full text-xs h-8"
                  style={{ paddingLeft: '2rem' }}
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <select
                value={selectedQuality}
                onChange={(e) => setSelectedQuality(e.target.value)}
                className="input-field text-xs h-8 min-w-[100px]"
              >
                {uniqueQualityTags.map(tag => (
                  <option key={tag} value={tag}>
                    {tag === 'all' ? 'All Quality' : tag}
                  </option>
                ))}
              </select>
            
              <select
                value={selectedMember}
                onChange={(e) => setSelectedMember(e.target.value)}
                className="input-field text-xs h-8 min-w-[100px]"
              >
                {uniqueMembers.map(member => (
                  <option key={member} value={member}>
                    {member === 'all' ? 'All Members' : member}
                  </option>
                ))}
              </select>
              
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {filteredCompanies.length} results
              </span>
            </div>
          </div>
        </div>
        )}
        {activeTab !== 'unlisted' && activeTab !== 'merged' && activeTab !== 'private' && activeTab !== 'pledge' && activeTab !== 'holdings' && (
        <>
        {/* Mobile Card View - visible only on small screens */}
        <div className="sm:hidden space-y-2">
          {filteredCompanies.map((company) => {
            const isExpanded = expandedCards.has(company.symbol);
            const gainPercent = company.gain_percent || 0;
            const isPositive = gainPercent >= 0;
            
            return (
              <div 
                key={company.symbol}
                className="card rounded-lg border border-gray-200 overflow-hidden"
                data-testid={`mobile-card-${company.symbol}`}
              >
                {/* Card Header - Always visible */}
                <div 
                  className="px-3 py-2.5 bg-gray-50 cursor-pointer active:bg-gray-100 transition-colors"
                  onClick={() => toggleCardExpand(company.symbol)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-primary text-sm truncate">{company.symbol}</div>
                        <div className="text-xs text-gray-500 truncate">{company.display_name}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="font-bold text-sm">{formatValue(company.total_value || 0)}</div>
                        <div className={`text-xs font-medium flex items-center justify-end gap-0.5 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                          {isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                          {gainPercent.toFixed(1)}%
                        </div>
                      </div>
                      <ChevronDown 
                        size={18} 
                        className={`text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} 
                      />
                    </div>
                  </div>
                </div>
                
                {/* Card Expanded Content */}
                {isExpanded && (
                  <div className="px-3 py-2.5 border-t border-gray-100 bg-white space-y-2">
                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-gray-50 rounded p-2">
                        <div className="text-gray-500 mb-0.5">Quantity</div>
                        <div className="font-semibold">{(company.total_quantity || 0).toLocaleString('en-IN')}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2">
                        <div className="text-gray-500 mb-0.5">Current Price</div>
                        <div className="font-semibold">₹{(company.current_price || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2">
                        <div className="text-gray-500 mb-0.5">Avg Cost</div>
                        <div className="font-semibold">₹{(company.avg_price || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</div>
                      </div>
                      <div className={`rounded p-2 ${isPositive ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="text-gray-500 mb-0.5">Total Gain</div>
                        <div className={`font-semibold ${isPositive ? 'text-green-700' : 'text-red-700'}`}>
                          {formatValue(company.gain || 0)}
                        </div>
                      </div>
                    </div>
                    
                    {/* Quality & Status Row */}
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      {company.quality_tags?.length > 0 && (
                        <span className={`px-2 py-0.5 rounded-full ${getQualityColor(company.quality_tags[0])}`}>
                          {mapQualityName(company.quality_tags[0])}
                        </span>
                      )}
                      {company.pledged_quantity > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                          🔒 Pledged: {company.pledged_quantity.toLocaleString()}
                        </span>
                      )}
                      <span className="text-gray-400">{company.holdings_count} holding{company.holdings_count > 1 ? 's' : ''}</span>
                    </div>
                    
                    {/* Notes Preview */}
                    {stockNotes[company.symbol] && (
                      <div className="text-xs text-gray-600 bg-blue-50 rounded p-2 line-clamp-2">
                        📝 {stockNotes[company.symbol]}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStockClick(company.symbol);
                        }}
                      >
                        <Sparkles size={12} className="mr-1" /> Analyze
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewHoldings(company);
                        }}
                      >
                        <Eye size={12} className="mr-1" /> Holdings
                      </Button>
                      {company.notes && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-xs px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setViewingNotesStock(company);
                            setShowNotesModal(true);
                          }}
                        >
                          📝
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Desktop Table View - hidden on small screens */}
        <div className="hidden sm:block card relative flex-1 min-h-0">
          {/* Scrollable container - flex-1 takes remaining space, min-h-0 allows shrinking */}
          <div className="h-full overflow-scroll always-scroll">
            <table className="w-full border-collapse" style={{ minWidth: '1000px' }}>
            <thead className="sticky top-0 z-30">
              <tr className="border-b bg-gray-50">
                <th 
                  className="text-left py-2 sm:py-3 px-2 sm:px-3 font-semibold cursor-pointer hover:bg-gray-100 sticky left-0 bg-gray-50 z-40 text-xs sm:text-sm shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                  onClick={() => handleSort('symbol')}
                >
                  <div className="flex items-center gap-1">
                    Symbol <ArrowUpDown size={12} className="hidden sm:inline" />
                  </div>
                </th>
                <th 
                  className="text-right py-2 sm:py-3 px-2 sm:px-3 font-semibold cursor-pointer hover:bg-gray-100 bg-gray-50 text-xs sm:text-sm"
                  onClick={() => handleSort('total_quantity')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Qty <ArrowUpDown size={12} className="hidden sm:inline" />
                  </div>
                </th>
                <th 
                  className="text-right py-2 sm:py-3 px-2 sm:px-3 font-semibold cursor-pointer hover:bg-gray-100 bg-gray-50 text-xs sm:text-sm"
                  onClick={() => handleSort('current_price')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Price <ArrowUpDown size={12} className="hidden sm:inline" />
                  </div>
                </th>
                <th 
                  className="text-right py-2 sm:py-3 px-2 sm:px-3 font-semibold cursor-pointer hover:bg-gray-100 bg-gray-50 text-xs sm:text-sm"
                  onClick={() => handleSort('total_value')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Value <ArrowUpDown size={12} className="hidden sm:inline" />
                  </div>
                </th>
                <th 
                  className="text-right py-2 sm:py-3 px-2 sm:px-3 font-semibold cursor-pointer hover:bg-gray-100 bg-gray-50 text-xs sm:text-sm"
                  onClick={() => handleSort('gain_percent')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Gain% <ArrowUpDown size={12} className="hidden sm:inline" />
                  </div>
                </th>
                <th 
                  className="text-center py-2 sm:py-3 px-2 sm:px-3 font-semibold cursor-pointer hover:bg-gray-100 bg-gray-50 text-xs sm:text-sm"
                  onClick={() => handleSort('quality_tag')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Quality <ArrowUpDown size={12} />
                  </div>
                </th>
                <th 
                  className="text-center py-2 sm:py-3 px-2 sm:px-3 font-semibold cursor-pointer hover:bg-gray-100 bg-gray-50 text-xs sm:text-sm"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center justify-center gap-1">
                    Status <ArrowUpDown size={12} className="hidden sm:inline" />
                  </div>
                </th>
                <th className="text-left py-2 sm:py-3 px-2 sm:px-3 font-semibold min-w-[120px] bg-gray-50 text-xs sm:text-sm">Notes</th>
                <th className="text-center py-2 sm:py-3 px-2 sm:px-3 font-semibold text-xs sm:text-sm sticky right-0 bg-gray-50 z-40 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] min-w-[100px]">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((company) => (
                <React.Fragment key={company.symbol}>
                <tr 
                  className={`border-b hover:bg-gray-50 transition-all duration-300 ${
                    expandedEditRow === company.symbol ? 'bg-blue-50' : ''
                  } ${
                    highlightedSymbol === company.symbol ? 'bg-yellow-100 ring-2 ring-yellow-400 ring-inset' : ''
                  }`}
                  id={`stock-row-${company.symbol}`}
                >
                  <td className={`py-2 sm:py-3 px-2 sm:px-3 sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${
                    highlightedSymbol === company.symbol ? 'bg-yellow-100' : expandedEditRow === company.symbol ? 'bg-blue-50' : 'bg-white'
                  }`}>
                    <div className="font-bold text-primary text-xs sm:text-sm">{company.symbol}</div>
                    <div 
                      className="text-xs text-gray-500 hover:text-primary hover:underline cursor-pointer flex items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewHoldings(company);
                      }}
                    >
                      <Eye size={10} className="sm:w-3 sm:h-3" />
                      {company.holdings_count} <span className="hidden sm:inline">holding{company.holdings_count > 1 ? 's' : ''}</span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-right font-medium">
                    {company.total_quantity?.toLocaleString('en-IN') || 0}
                  </td>
                  <td className="py-3 px-3 text-right">
                    ₹{(company.current_price || 0).toFixed(2)}
                  </td>
                  <td className="py-3 px-3 text-right font-semibold">
                    {formatValue(company.total_value || 0)}
                  </td>
                  <td className="py-3 px-3 text-right">
                    <span className={`font-semibold ${(company.gain_percent || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(company.gain_percent || 0) >= 0 ? '+' : ''}{(company.gain_percent || 0).toFixed(2)}%
                    </span>
                    <div className={`text-xs ${(company.gain || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {(company.gain || 0) >= 0 ? '+' : ''}{formatValue(company.gain || 0)}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center">
                    <div className="flex flex-wrap gap-1 justify-center">
                      {company.quality_tags?.slice(0, 2).map((tag, idx) => (
                        <span key={idx} className={`px-2 py-0.5 rounded-full text-xs font-medium ${getQualityColor(tag)}`}>
                          {mapQualityName(tag)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-center">
                    {company.pledged_quantity > 0 ? (
                      <span className="badge badge-warning text-xs">
                        {company.pledged_quantity} Pledged
                      </span>
                    ) : (
                      <span className="badge badge-success text-xs">Free</span>
                    )}
                  </td>
                  {/* Notes Column - Click to View */}
                  <td className="py-3 px-3">
                    {editMode ? (
                      <textarea
                        placeholder="Add notes..."
                        defaultValue={stockNotes[company.symbol] || company.notes || ''}
                        onBlur={(e) => {
                          const currentNotes = stockNotes[company.symbol] || company.notes || '';
                          if (e.target.value !== currentNotes) {
                            saveNotes(company.symbol, e.target.value);
                          }
                        }}
                        rows={2}
                        className="w-full min-w-[120px] px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400 bg-gray-50 focus:bg-white resize-y"
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setViewingNotesStock({...company, display_name: company.symbol});
                          setShowNotesModal(true);
                        }}
                        className="w-full min-w-[100px] max-w-[180px] text-left group"
                      >
                        {(stockNotes[company.symbol] || company.notes) ? (
                          <div className="px-2 py-1.5 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors">
                            <p className="text-xs text-blue-800 line-clamp-2 font-medium">
                              {(stockNotes[company.symbol] || company.notes).split('\n')[0].replace('STATUS:', '').slice(0, 50)}
                              {(stockNotes[company.symbol] || company.notes).length > 50 ? '...' : ''}
                            </p>
                            <p className="text-[10px] text-blue-500 mt-0.5 group-hover:underline">Click to view →</p>
                          </div>
                        ) : (
                          <div className="px-2 py-1.5 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 text-center transition-colors">
                            <p className="text-xs text-gray-400">No notes</p>
                            <p className="text-[10px] text-gray-400 group-hover:text-gray-500">Click to add</p>
                          </div>
                        )}
                      </button>
                    )}
                  </td>
                  {/* Action Column - Merged Edit & Analyze */}
                  <td className={`py-3 px-3 text-center sticky right-0 z-10 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] ${
                    highlightedSymbol === company.symbol ? 'bg-yellow-100' : expandedEditRow === company.symbol ? 'bg-blue-50' : 'bg-white'
                  }`}>
                    {editMode ? (
                      <div className="flex flex-col gap-1">
                        <select
                          className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-400 cursor-pointer"
                          value=""
                          onChange={(e) => {
                            const action = e.target.value;
                            if (action === 'analyze') {
                              handleStockClick(company.symbol);
                            } else if (action === 'record_transaction') {
                              openTransactionDialog(company.symbol);
                            } else if (action === 'fix_ticker') {
                              setExpandedEditRow(expandedEditRow === company.symbol ? null : company.symbol);
                              setEditFormData(prev => ({ ...prev, [company.symbol]: { ...prev[company.symbol], action: 'fix_ticker' } }));
                            } else if (action === 'merged') {
                              setExpandedEditRow(expandedEditRow === company.symbol ? null : company.symbol);
                              setEditFormData(prev => ({ ...prev, [company.symbol]: { ...prev[company.symbol], action: 'merged' } }));
                            } else if (action === 'private') {
                              setExpandedEditRow(expandedEditRow === company.symbol ? null : company.symbol);
                              setEditFormData(prev => ({ ...prev, [company.symbol]: { ...prev[company.symbol], action: 'private' } }));
                            } else if (action === 'unlisted') {
                              markAsUnlisted(company.symbol);
                            }
                            e.target.value = '';
                          }}
                          disabled={savingAction === company.symbol}
                        >
                          <option value="">⚡ Actions</option>
                          <option value="analyze">📊 Analyze</option>
                          <option value="record_transaction">📝 Record Transaction</option>
                          <option value="fix_ticker">✏️ Fix Ticker</option>
                          <option value="merged">🔄 Mark as Merged</option>
                          <option value="private">🟢 Mark as Private</option>
                          <option value="unlisted">🟡 Mark as Unlisted</option>
                        </select>
                        {savingAction === company.symbol && (
                          <RefreshCw size={12} className="animate-spin text-blue-500 mx-auto" />
                        )}
                      </div>
                    ) : (
                      <Button 
                        size="sm" 
                        className="btn-primary text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStockClick(company.symbol);
                        }}
                      >
                        Analyze <ChevronRight size={14} />
                      </Button>
                    )}
                  </td>
                </tr>
                {/* Expanded Edit Row */}
                {editMode && expandedEditRow === company.symbol && (
                  <tr className="bg-blue-50">
                    <td colSpan={9} className="py-3 px-4">
                      {/* Fix Ticker Form */}
                      {editFormData[company.symbol]?.action === 'fix_ticker' && (
                        <div className="space-y-3">
                          <p className="font-medium text-blue-800 text-sm">✏️ Fix Ticker for {company.symbol}</p>
                          <div className="flex gap-3 items-end">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Ticker Symbol</label>
                              <input
                                type="text"
                                placeholder="e.g. RELIANCE"
                                value={tickerInputs[company.symbol] || ''}
                                onChange={(e) => setTickerInputs(prev => ({ ...prev, [company.symbol]: e.target.value.toUpperCase() }))}
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400 uppercase w-40"
                              />
                            </div>
                            <Button size="sm" onClick={() => fixTickerMapping(company.symbol)} disabled={savingAction === company.symbol || !tickerInputs[company.symbol]} className="bg-blue-600 hover:bg-blue-700 text-white">
                              {savingAction === company.symbol ? <RefreshCw size={14} className="animate-spin" /> : 'Save Ticker'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                      {/* Merged Form */}
                      {editFormData[company.symbol]?.action === 'merged' && (
                        <div className="space-y-3">
                          <p className="font-medium text-blue-800 text-sm">🔄 Mark {company.symbol} as Merged</p>
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Company Name *</label>
                              <input type="text" placeholder="e.g. HDFC Bank" value={editFormData[company.symbol]?.newCompany || ''} onChange={(e) => handleEditFormChange(company.symbol, 'newCompany', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Ticker</label>
                              <input type="text" placeholder="e.g. HDFCBANK" value={editFormData[company.symbol]?.newTicker || ''} onChange={(e) => handleEditFormChange(company.symbol, 'newTicker', e.target.value.toUpperCase())} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400 uppercase" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Swap Ratio</label>
                              <input type="text" placeholder="e.g. 1:2" value={editFormData[company.symbol]?.swapRatio || ''} onChange={(e) => handleEditFormChange(company.symbol, 'swapRatio', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <input type="text" placeholder="Optional" value={editFormData[company.symbol]?.notes || ''} onChange={(e) => handleEditFormChange(company.symbol, 'notes', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400" />
                            </div>
                            <div className="flex items-end gap-2">
                              <Button size="sm" onClick={() => markAsMerged(company.symbol)} disabled={savingAction === company.symbol || !editFormData[company.symbol]?.newCompany} className="bg-blue-600 hover:bg-blue-700 text-white">
                                {savingAction === company.symbol ? <RefreshCw size={14} className="animate-spin" /> : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Private Form */}
                      {editFormData[company.symbol]?.action === 'private' && (
                        <div className="space-y-3">
                          <p className="font-medium text-emerald-800 text-sm">🟢 Mark {company.symbol} as Private</p>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Manual Share Price (₹)</label>
                              <input type="number" placeholder="e.g. 150.00" value={editFormData[company.symbol]?.price || ''} onChange={(e) => handleEditFormChange(company.symbol, 'price', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400" min="0" step="0.01" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <input type="text" placeholder="Why private, valuation source..." value={editFormData[company.symbol]?.notes || ''} onChange={(e) => handleEditFormChange(company.symbol, 'notes', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400" />
                            </div>
                            <div className="flex items-end gap-2">
                              <Button size="sm" onClick={() => markAsPrivate(company.symbol)} disabled={savingAction === company.symbol} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                {savingAction === company.symbol ? <RefreshCw size={14} className="animate-spin" /> : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">💡 Private stocks with manual prices will be included in your portfolio total value.</p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        </div>
        </>
        )}

        {/* Unlisted Stocks Table */}
        {activeTab === 'unlisted' && (
        <>
        {/* Mobile Cards for Unlisted */}
        <div className="sm:hidden space-y-2">
          {filteredCompanies.map((stock) => {
            const isExpanded = expandedCards.has(stock.display_name);
            
            return (
              <div 
                key={stock.display_name}
                className="card rounded-lg border border-red-200 overflow-hidden"
                data-testid={`mobile-unlisted-${stock.display_name}`}
              >
                {/* Card Header */}
                <div 
                  className="px-3 py-2.5 bg-red-50 cursor-pointer active:bg-red-100"
                  onClick={() => toggleCardExpand(stock.display_name)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-800 text-sm truncate">{stock.display_name}</div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          stock.reason === 'delisted' ? 'bg-red-100 text-red-700' :
                          stock.reason === 'merged' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {stock.reason?.charAt(0).toUpperCase() + stock.reason?.slice(1) || 'Unlisted'}
                        </span>
                        <span className="text-gray-400">
                          {(stock.total_quantity || 0).toLocaleString()} qty
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <div className="font-bold text-sm text-orange-700">{formatValue(stock.total_cost || 0)}</div>
                      </div>
                      <ChevronDown 
                        size={18} 
                        className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                      />
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-3 py-2.5 border-t border-red-100 bg-white space-y-2 text-sm">
                    {/* Swap Info */}
                    {stock.new_company && (
                      <div className="bg-green-50 rounded p-2">
                        <div className="text-xs text-green-700 font-medium">Merged Into</div>
                        <div className="font-semibold text-green-800">{stock.new_company}</div>
                        {stock.swap_ratio && <div className="text-xs text-blue-600">Ratio: {stock.swap_ratio}</div>}
                      </div>
                    )}
                    
                    {/* Members */}
                    {stock.members?.length > 0 && (
                      <div className="text-xs">
                        <span className="text-gray-500">Members: </span>
                        {stock.members.join(', ')}
                      </div>
                    )}
                    
                    {/* Notes */}
                    {stock.notes && (
                      <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        📝 {stock.notes}
                      </div>
                    )}
                    
                    {/* Date */}
                    <div className="text-xs text-gray-400">
                      Marked: {stock.marked_at ? new Date(stock.marked_at).toLocaleDateString() : 'Unknown'}
                    </div>
                    
                    {/* Action Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full h-8 text-xs border-green-300 text-green-700 hover:bg-green-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestoreToListed(stock.display_name);
                      }}
                      disabled={restoringStock === stock.display_name}
                    >
                      <RefreshCw size={12} className={`mr-1 ${restoringStock === stock.display_name ? 'animate-spin' : ''}`} />
                      Restore to Listed
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          
          {filteredCompanies.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle size={32} className="mx-auto mb-2 text-red-400" />
              <p className="font-medium">No unlisted stocks found</p>
            </div>
          )}
        </div>
        
        {/* Desktop Table */}
        <div className="hidden sm:block card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-red-50">
                <th className="text-left py-3 px-3 font-semibold">Company Name</th>
                <th className="text-right py-3 px-3 font-semibold">Qty</th>
                <th className="text-right py-3 px-3 font-semibold">Cost</th>
                <th className="text-center py-3 px-3 font-semibold">Status</th>
                <th className="text-left py-3 px-3 font-semibold">Why Unlisted / Swap Info</th>
                <th className="text-left py-3 px-3 font-semibold">Members</th>
                <th className="text-left py-3 px-3 font-semibold">Notes</th>
                <th className="text-center py-3 px-3 font-semibold">Correct Ticker</th>
                <th className="text-center py-3 px-3 font-semibold">Set Manual Price</th>
                {editMode && <th className="text-center py-3 px-3 font-semibold">Edit</th>}
                <th className="text-center py-3 px-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((stock) => (
                <React.Fragment key={stock.display_name}>
                <tr className={`border-b hover:bg-red-50/50 ${expandedEditRow === stock.display_name ? 'bg-red-50' : ''}`}>
                  <td className="py-3 px-3">
                    <div className="font-bold text-gray-800">{stock.display_name}</div>
                    <div className="text-xs text-gray-500">Marked on {stock.marked_at ? new Date(stock.marked_at).toLocaleDateString() : 'Unknown'}</div>
                  </td>
                  <td className="text-right py-3 px-3 font-medium">
                    {(stock.total_quantity || 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 font-medium text-orange-700">
                    {formatValue(stock.total_cost || 0)}
                  </td>
                  <td className="text-center py-3 px-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      stock.reason === 'delisted' ? 'bg-red-100 text-red-700' :
                      stock.reason === 'merged' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {stock.reason?.charAt(0).toUpperCase() + stock.reason?.slice(1) || 'Unlisted'}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    {stock.new_company ? (
                      <div>
                        <div className="font-medium text-gray-800">{stock.new_company}</div>
                        {stock.swap_ratio && (
                          <div className="text-xs text-blue-600">Ratio: {stock.swap_ratio}</div>
                        )}
                        {stock.new_company_status && (
                          <div className={`text-xs mt-0.5 ${
                            stock.new_company_status.toLowerCase().includes('listed') && !stock.new_company_status.toLowerCase().includes('unlisted')
                              ? 'text-green-600 font-medium' 
                              : 'text-gray-500'
                          }`}>
                            {stock.new_company_status}
                          </div>
                        )}
                      </div>
                    ) : stock.fix_note ? (
                      <div className="text-xs text-amber-600">
                        {stock.fix_note}
                        {stock.previous_wrong_price && (
                          <div className="text-gray-400">Was priced at ₹{stock.previous_wrong_price.toLocaleString()}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs">No trading data available</span>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex flex-wrap gap-1">
                      {stock.members?.slice(0, 2).map((m, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{m}</span>
                      ))}
                      {stock.members?.length > 2 && (
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">+{stock.members.length - 2}</span>
                      )}
                    </div>
                  </td>
                  {/* Notes Column - Click to View */}
                  <td className="py-3 px-3">
                    {editMode ? (
                      <textarea
                        placeholder="Add notes..."
                        defaultValue={stockNotes[stock.display_name] || stock.notes || ''}
                        onBlur={(e) => {
                          const currentNotes = stockNotes[stock.display_name] || stock.notes || '';
                          if (e.target.value !== currentNotes) {
                            saveNotes(stock.display_name, e.target.value);
                          }
                        }}
                        rows={2}
                        className="w-full min-w-[120px] px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-red-400 bg-gray-50 focus:bg-white resize-y"
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setViewingNotesStock(stock);
                          setShowNotesModal(true);
                        }}
                        className="w-full min-w-[100px] max-w-[180px] text-left group"
                      >
                        {(stockNotes[stock.display_name] || stock.notes) ? (
                          <div className="px-2 py-1.5 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors">
                            <p className="text-xs text-blue-800 line-clamp-2 font-medium">
                              {(stockNotes[stock.display_name] || stock.notes).split('\n')[0].replace('STATUS:', '').slice(0, 50)}
                              {(stockNotes[stock.display_name] || stock.notes).length > 50 ? '...' : ''}
                            </p>
                            <p className="text-[10px] text-blue-500 mt-0.5 group-hover:underline">Click to view →</p>
                          </div>
                        ) : (
                          <div className="px-2 py-1.5 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 text-center transition-colors">
                            <p className="text-xs text-gray-400">No notes</p>
                            <p className="text-[10px] text-gray-400 group-hover:text-gray-500">Click to add</p>
                          </div>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="text"
                      placeholder="e.g. TCS"
                      value={tickerInputs[stock.display_name] || ''}
                      onChange={(e) => handleTickerInputChange(stock.display_name, e.target.value)}
                      className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-400 uppercase"
                    />
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1">
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
                        <input
                          type="number"
                          placeholder={stock.manual_price ? stock.manual_price.toLocaleString() : "0"}
                          value={priceInputs[stock.display_name] || ''}
                          onChange={(e) => handlePriceInputChange(stock.display_name, e.target.value)}
                          className="w-20 pl-5 pr-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:border-purple-400"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSavePrice(stock.display_name)}
                        disabled={savingPrice === stock.display_name || !priceInputs[stock.display_name]}
                        className={`text-xs px-2 py-1 h-7 ${priceInputs[stock.display_name] ? 'bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200' : 'text-gray-400'}`}
                      >
                        {savingPrice === stock.display_name ? (
                          <RefreshCw size={12} className="animate-spin" />
                        ) : (
                          'Save'
                        )}
                      </Button>
                    </div>
                    {stock.manual_price && (
                      <div className="text-xs text-purple-600 mt-1">
                        Current: ₹{stock.manual_price.toLocaleString()}
                      </div>
                    )}
                  </td>
                  {/* Edit Column - only when edit mode is active */}
                  {editMode && (
                  <td className="py-3 px-3 text-center">
                    <select
                      className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:border-red-400 cursor-pointer"
                      value=""
                      onChange={(e) => {
                        const action = e.target.value;
                        if (action === 'fix_ticker') {
                          setExpandedEditRow(expandedEditRow === stock.display_name ? null : stock.display_name);
                          setEditFormData(prev => ({ ...prev, [stock.display_name]: { ...prev[stock.display_name], action: 'fix_ticker' } }));
                        } else if (action === 'merged') {
                          setExpandedEditRow(expandedEditRow === stock.display_name ? null : stock.display_name);
                          setEditFormData(prev => ({ ...prev, [stock.display_name]: { ...prev[stock.display_name], action: 'merged' } }));
                        } else if (action === 'private') {
                          setExpandedEditRow(expandedEditRow === stock.display_name ? null : stock.display_name);
                          setEditFormData(prev => ({ ...prev, [stock.display_name]: { ...prev[stock.display_name], action: 'private' } }));
                        }
                        e.target.value = '';
                      }}
                      disabled={savingAction === stock.display_name}
                    >
                      <option value="">⚡ Edit</option>
                      <option value="fix_ticker">✏️ Fix Ticker</option>
                      <option value="merged">🔄 Mark as Merged</option>
                      <option value="private">🟢 Mark as Private</option>
                    </select>
                    {savingAction === stock.display_name && (
                      <RefreshCw size={12} className="inline ml-1 animate-spin text-red-500" />
                    )}
                  </td>
                  )}
                  <td className="text-center py-3 px-3">
                    <Button
                      size="sm"
                      variant={tickerInputs[stock.display_name] ? "default" : "outline"}
                      onClick={() => handleRestoreToListed(stock.display_name)}
                      disabled={restoringStock === stock.display_name}
                      className={`text-xs ${tickerInputs[stock.display_name] ? 'bg-green-600 hover:bg-green-700' : 'text-blue-600 hover:bg-blue-50 border-blue-200'}`}
                    >
                      {restoringStock === stock.display_name ? (
                        <RefreshCw size={12} className="animate-spin" />
                      ) : tickerInputs[stock.display_name] ? (
                        'Restore & Map'
                      ) : (
                        'Restore'
                      )}
                    </Button>
                  </td>
                </tr>
                {/* Expanded Edit Row for Unlisted */}
                {editMode && expandedEditRow === stock.display_name && (
                  <tr className="bg-red-50">
                    <td colSpan={editMode ? 12 : 11} className="py-3 px-4">
                      {/* Fix Ticker Form */}
                      {editFormData[stock.display_name]?.action === 'fix_ticker' && (
                        <div className="space-y-3">
                          <p className="font-medium text-red-800 text-sm">✏️ Fix Ticker for {stock.display_name}</p>
                          <div className="flex gap-3 items-end">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Ticker Symbol</label>
                              <input
                                type="text"
                                placeholder="e.g. RELIANCE"
                                value={tickerInputs[stock.display_name] || ''}
                                onChange={(e) => handleTickerInputChange(stock.display_name, e.target.value)}
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-red-400 uppercase w-40"
                              />
                            </div>
                            <Button size="sm" onClick={() => fixTickerMapping(stock.display_name)} disabled={savingAction === stock.display_name || !tickerInputs[stock.display_name]} className="bg-red-600 hover:bg-red-700 text-white">
                              {savingAction === stock.display_name ? <RefreshCw size={14} className="animate-spin" /> : 'Save Ticker'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                      {/* Merged Form */}
                      {editFormData[stock.display_name]?.action === 'merged' && (
                        <div className="space-y-3">
                          <p className="font-medium text-red-800 text-sm">🔄 Mark {stock.display_name} as Merged</p>
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Company Name *</label>
                              <input type="text" placeholder="e.g. HDFC Bank" value={editFormData[stock.display_name]?.newCompany || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'newCompany', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-red-400" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Ticker</label>
                              <input type="text" placeholder="e.g. HDFCBANK" value={editFormData[stock.display_name]?.newTicker || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'newTicker', e.target.value.toUpperCase())} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-red-400 uppercase" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Swap Ratio</label>
                              <input type="text" placeholder="e.g. 1:2" value={editFormData[stock.display_name]?.swapRatio || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'swapRatio', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-red-400" />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <input type="text" placeholder="Optional" value={editFormData[stock.display_name]?.notes || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'notes', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-red-400" />
                            </div>
                            <div className="flex items-end gap-2">
                              <Button size="sm" onClick={() => markAsMerged(stock.display_name)} disabled={savingAction === stock.display_name || !editFormData[stock.display_name]?.newCompany} className="bg-red-600 hover:bg-red-700 text-white">
                                {savingAction === stock.display_name ? <RefreshCw size={14} className="animate-spin" /> : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Private Form */}
                      {editFormData[stock.display_name]?.action === 'private' && (
                        <div className="space-y-3">
                          <p className="font-medium text-emerald-800 text-sm">🟢 Mark {stock.display_name} as Private</p>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Manual Share Price (₹)</label>
                              <input type="number" placeholder="e.g. 150.00" value={editFormData[stock.display_name]?.price || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'price', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400" min="0" step="0.01" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <input type="text" placeholder="Why private, valuation source..." value={editFormData[stock.display_name]?.notes || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'notes', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400" />
                            </div>
                            <div className="flex items-end gap-2">
                              <Button size="sm" onClick={() => markAsPrivate(stock.display_name)} disabled={savingAction === stock.display_name} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                {savingAction === stock.display_name ? <RefreshCw size={14} className="animate-spin" /> : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">💡 Private stocks with manual prices will be included in your portfolio total value.</p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {filteredCompanies.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <AlertTriangle size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No unlisted stocks found</p>
              <p className="text-sm mt-1">Mark stocks as unlisted from the Imports → Symbols page</p>
            </div>
          )}
        </div>
        </>
        )}

        {/* Merged Stocks Table */}
        {activeTab === 'merged' && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-blue-50">
                <th className="text-left py-3 px-3 font-semibold">Original Company</th>
                <th className="text-left py-3 px-3 font-semibold">Merged Into</th>
                <th className="text-center py-3 px-3 font-semibold">Swap Ratio</th>
                <th className="text-right py-3 px-3 font-semibold">Original Qty</th>
                <th className="text-right py-3 px-3 font-semibold">Cost Basis</th>
                <th className="text-left py-3 px-3 font-semibold">Members</th>
                <th className="text-left py-3 px-3 font-semibold">Notes</th>
                {editMode && <th className="text-center py-3 px-3 font-semibold">Edit</th>}
                <th className="text-center py-3 px-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((stock) => (
                <React.Fragment key={stock.display_name}>
                <tr className={`border-b hover:bg-blue-50/50 ${expandedEditRow === stock.display_name ? 'bg-blue-50' : ''}`}>
                  <td className="py-3 px-3">
                    <div className="font-bold text-gray-800">{stock.display_name}</div>
                    {stock.merger_date && (
                      <div className="text-xs text-gray-500">Merged on {new Date(stock.merger_date).toLocaleDateString()}</div>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <div className="font-medium text-blue-700">{stock.new_company_name || '-'}</div>
                    {stock.new_ticker && (
                      <div className="text-xs text-gray-500">Ticker: {stock.new_ticker}</div>
                    )}
                  </td>
                  <td className="text-center py-3 px-3">
                    {stock.swap_ratio ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium text-sm">
                        {stock.swap_ratio}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="text-right py-3 px-3 font-medium">
                    {(stock.total_quantity || 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 font-medium text-blue-700">
                    {formatValue(stock.total_cost || 0)}
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex flex-wrap gap-1">
                      {stock.members?.slice(0, 2).map((m, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{m}</span>
                      ))}
                      {stock.members?.length > 2 && (
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">+{stock.members.length - 2}</span>
                      )}
                    </div>
                  </td>
                  {/* Notes Column - Click to View */}
                  <td className="py-3 px-3">
                    {editMode ? (
                      <textarea
                        placeholder="Add notes..."
                        defaultValue={stockNotes[stock.display_name] || stock.merger_notes || stock.notes || ''}
                        onBlur={(e) => {
                          const currentNotes = stockNotes[stock.display_name] || stock.merger_notes || stock.notes || '';
                          if (e.target.value !== currentNotes) {
                            saveNotes(stock.display_name, e.target.value);
                          }
                        }}
                        rows={2}
                        className="w-full min-w-[120px] px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-blue-400 bg-gray-50 focus:bg-white resize-y"
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setViewingNotesStock(stock);
                          setShowNotesModal(true);
                        }}
                        className="w-full min-w-[100px] max-w-[180px] text-left group"
                      >
                        {(stockNotes[stock.display_name] || stock.merger_notes || stock.notes) ? (
                          <div className="px-2 py-1.5 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200 transition-colors">
                            <p className="text-xs text-blue-800 line-clamp-2 font-medium">
                              {(stockNotes[stock.display_name] || stock.merger_notes || stock.notes).split('\n')[0].replace('STATUS:', '').slice(0, 50)}
                              {(stockNotes[stock.display_name] || stock.merger_notes || stock.notes).length > 50 ? '...' : ''}
                            </p>
                            <p className="text-[10px] text-blue-500 mt-0.5 group-hover:underline">Click to view →</p>
                          </div>
                        ) : (
                          <div className="px-2 py-1.5 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 text-center transition-colors">
                            <p className="text-xs text-gray-400">No notes</p>
                            <p className="text-[10px] text-gray-400 group-hover:text-gray-500">Click to add</p>
                          </div>
                        )}
                      </button>
                    )}
                  </td>
                  {/* Edit Column - only when edit mode is active */}
                  {editMode && (
                  <td className="py-3 px-3 text-center">
                    <select
                      className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:border-blue-400 cursor-pointer"
                      value=""
                      onChange={(e) => {
                        const action = e.target.value;
                        if (action === 'fix_ticker') {
                          setExpandedEditRow(expandedEditRow === stock.display_name ? null : stock.display_name);
                          setEditFormData(prev => ({ ...prev, [stock.display_name]: { ...prev[stock.display_name], action: 'fix_ticker' } }));
                        } else if (action === 'private') {
                          setExpandedEditRow(expandedEditRow === stock.display_name ? null : stock.display_name);
                          setEditFormData(prev => ({ ...prev, [stock.display_name]: { ...prev[stock.display_name], action: 'private' } }));
                        } else if (action === 'unlisted') {
                          markAsUnlisted(stock.display_name);
                        } else if (action === 'restore') {
                          handleRestoreToListed(stock.display_name);
                        }
                        e.target.value = '';
                      }}
                      disabled={savingAction === stock.display_name}
                    >
                      <option value="">⚡ Edit</option>
                      <option value="fix_ticker">✏️ Fix Ticker</option>
                      <option value="private">🟢 Mark as Private</option>
                      <option value="unlisted">🟡 Mark as Unlisted</option>
                      <option value="restore">↩️ Restore to Listed</option>
                    </select>
                    {savingAction === stock.display_name && (
                      <RefreshCw size={12} className="inline ml-1 animate-spin text-blue-500" />
                    )}
                  </td>
                  )}
                  <td className="text-center py-3 px-3">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-xs text-blue-600 hover:bg-blue-50 border-blue-200"
                      onClick={() => handleRestoreToListed(stock.display_name)}
                      disabled={restoringStock === stock.display_name}
                    >
                      {restoringStock === stock.display_name ? (
                        <RefreshCw size={12} className="animate-spin" />
                      ) : (
                        'Restore'
                      )}
                    </Button>
                  </td>
                </tr>
                {/* Expanded Edit Row for Merged */}
                {editMode && expandedEditRow === stock.display_name && (
                  <tr className="bg-blue-50">
                    <td colSpan={editMode ? 10 : 9} className="py-3 px-4">
                      {/* Fix Ticker Form */}
                      {editFormData[stock.display_name]?.action === 'fix_ticker' && (
                        <div className="space-y-3">
                          <p className="font-medium text-blue-800 text-sm">✏️ Fix Ticker for {stock.display_name}</p>
                          <div className="flex gap-3 items-end">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Ticker Symbol</label>
                              <input
                                type="text"
                                placeholder="e.g. RELIANCE"
                                value={tickerInputs[stock.display_name] || ''}
                                onChange={(e) => handleTickerInputChange(stock.display_name, e.target.value)}
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400 uppercase w-40"
                              />
                            </div>
                            <Button size="sm" onClick={() => fixTickerMapping(stock.display_name)} disabled={savingAction === stock.display_name || !tickerInputs[stock.display_name]} className="bg-blue-600 hover:bg-blue-700 text-white">
                              {savingAction === stock.display_name ? <RefreshCw size={14} className="animate-spin" /> : 'Save Ticker'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                      {/* Private Form */}
                      {editFormData[stock.display_name]?.action === 'private' && (
                        <div className="space-y-3">
                          <p className="font-medium text-emerald-800 text-sm">🟢 Mark {stock.display_name} as Private</p>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">Manual Share Price (₹)</label>
                              <input type="number" placeholder="e.g. 150.00" value={editFormData[stock.display_name]?.price || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'price', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400" min="0" step="0.01" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <input type="text" placeholder="Why private, valuation source..." value={editFormData[stock.display_name]?.notes || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'notes', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400" />
                            </div>
                            <div className="flex items-end gap-2">
                              <Button size="sm" onClick={() => markAsPrivate(stock.display_name)} disabled={savingAction === stock.display_name} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                {savingAction === stock.display_name ? <RefreshCw size={14} className="animate-spin" /> : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">💡 Private stocks with manual prices will be included in your portfolio total value.</p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {filteredCompanies.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Building2 size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No merged stocks found</p>
              <p className="text-sm mt-1">Mark stocks as merged from the Imports → Symbols page</p>
            </div>
          )}
        </div>
        )}

        {/* Private Stocks Table */}
        {activeTab === 'private' && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-emerald-50">
                <th className="text-left py-3 px-3 font-semibold">Company Name</th>
                <th className="text-right py-3 px-3 font-semibold">Qty</th>
                <th className="text-right py-3 px-3 font-semibold">Manual Price</th>
                <th className="text-right py-3 px-3 font-semibold">Current Value</th>
                <th className="text-right py-3 px-3 font-semibold">Cost Basis</th>
                <th className="text-center py-3 px-3 font-semibold">Source</th>
                <th className="text-left py-3 px-3 font-semibold">Notes</th>
                <th className="text-left py-3 px-3 font-semibold">Members</th>
                {editMode && <th className="text-center py-3 px-3 font-semibold">Edit</th>}
                <th className="text-center py-3 px-3 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.map((stock) => (
                <React.Fragment key={stock.display_name}>
                <tr className={`border-b hover:bg-emerald-50/50 ${expandedEditRow === stock.display_name ? 'bg-emerald-50' : ''}`}>
                  <td className="py-3 px-3">
                    <div className="font-bold text-gray-800">{stock.display_name}</div>
                    {stock.manual_price_updated_at && (
                      <div className="text-xs text-gray-500">Price updated: {new Date(stock.manual_price_updated_at).toLocaleDateString()}</div>
                    )}
                  </td>
                  <td className="text-right py-3 px-3 font-medium">
                    {(stock.total_quantity || 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3">
                    {stock.manual_price ? (
                      <span className="font-bold text-emerald-700">₹{stock.manual_price.toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-400">Not set</span>
                    )}
                  </td>
                  <td className="text-right py-3 px-3 font-bold text-emerald-700">
                    {formatValue(stock.total_current_value || 0)}
                  </td>
                  <td className="text-right py-3 px-3 text-gray-600">
                    {formatValue(stock.total_cost || 0)}
                  </td>
                  <td className="text-center py-3 px-3">
                    {stock.was_merged ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        Post-Merger
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs">
                        Direct Private
                      </span>
                    )}
                    {stock.merged_into && (
                      <div className="text-xs text-gray-500 mt-1">from {stock.merged_into}</div>
                    )}
                  </td>
                  {/* Notes Column - Click to View */}
                  <td className="py-3 px-3">
                    {editMode ? (
                      <textarea
                        placeholder="Add notes..."
                        defaultValue={stockNotes[stock.display_name] || stock.notes || ''}
                        onBlur={(e) => {
                          const currentNotes = stockNotes[stock.display_name] || stock.notes || '';
                          if (e.target.value !== currentNotes) {
                            saveNotes(stock.display_name, e.target.value);
                          }
                        }}
                        rows={2}
                        className="w-full min-w-[120px] px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:border-emerald-400 bg-gray-50 focus:bg-white resize-y"
                      />
                    ) : (
                      <button
                        onClick={() => {
                          setViewingNotesStock(stock);
                          setShowNotesModal(true);
                        }}
                        className="w-full min-w-[100px] max-w-[180px] text-left group"
                      >
                        {(stockNotes[stock.display_name] || stock.notes) ? (
                          <div className="px-2 py-1.5 bg-emerald-50 hover:bg-emerald-100 rounded border border-emerald-200 transition-colors">
                            <p className="text-xs text-emerald-800 line-clamp-2 font-medium">
                              {(stockNotes[stock.display_name] || stock.notes).split('\n')[0].replace('STATUS:', '').slice(0, 50)}
                              {(stockNotes[stock.display_name] || stock.notes).length > 50 ? '...' : ''}
                            </p>
                            <p className="text-[10px] text-emerald-500 mt-0.5 group-hover:underline">Click to view →</p>
                          </div>
                        ) : (
                          <div className="px-2 py-1.5 bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 text-center transition-colors">
                            <p className="text-xs text-gray-400">No notes</p>
                            <p className="text-[10px] text-gray-400 group-hover:text-gray-500">Click to add</p>
                          </div>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex flex-wrap gap-1">
                      {stock.members?.slice(0, 2).map((m, i) => (
                        <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{m}</span>
                      ))}
                      {stock.members?.length > 2 && (
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">+{stock.members.length - 2}</span>
                      )}
                    </div>
                  </td>
                  {/* Edit Column - only when edit mode is active */}
                  {editMode && (
                  <td className="py-3 px-3 text-center">
                    <select
                      className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:border-emerald-400 cursor-pointer"
                      value=""
                      onChange={(e) => {
                        const action = e.target.value;
                        if (action === 'fix_ticker') {
                          setExpandedEditRow(expandedEditRow === stock.display_name ? null : stock.display_name);
                          setEditFormData(prev => ({ ...prev, [stock.display_name]: { ...prev[stock.display_name], action: 'fix_ticker' } }));
                        } else if (action === 'update_price') {
                          setExpandedEditRow(expandedEditRow === stock.display_name ? null : stock.display_name);
                          setEditFormData(prev => ({ ...prev, [stock.display_name]: { ...prev[stock.display_name], action: 'update_price' } }));
                        } else if (action === 'unlisted') {
                          markAsUnlisted(stock.display_name);
                        } else if (action === 'restore') {
                          handleRestoreToListed(stock.display_name);
                        }
                        e.target.value = '';
                      }}
                      disabled={savingAction === stock.display_name}
                    >
                      <option value="">⚡ Edit</option>
                      <option value="fix_ticker">✏️ Fix Ticker</option>
                      <option value="update_price">💰 Update Price</option>
                      <option value="unlisted">🟡 Mark as Unlisted</option>
                      <option value="restore">↩️ Restore to Listed</option>
                    </select>
                    {savingAction === stock.display_name && (
                      <RefreshCw size={12} className="inline ml-1 animate-spin text-emerald-500" />
                    )}
                  </td>
                  )}
                  <td className="text-center py-3 px-3">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="text-xs text-emerald-600 hover:bg-emerald-50 border-emerald-200"
                      onClick={() => handleRestoreToListed(stock.display_name)}
                      disabled={restoringStock === stock.display_name}
                    >
                      {restoringStock === stock.display_name ? (
                        <RefreshCw size={12} className="animate-spin" />
                      ) : (
                        'Restore'
                      )}
                    </Button>
                  </td>
                </tr>
                {/* Expanded Edit Row for Private */}
                {editMode && expandedEditRow === stock.display_name && (
                  <tr className="bg-emerald-50">
                    <td colSpan={editMode ? 11 : 10} className="py-3 px-4">
                      {/* Fix Ticker Form */}
                      {editFormData[stock.display_name]?.action === 'fix_ticker' && (
                        <div className="space-y-3">
                          <p className="font-medium text-emerald-800 text-sm">✏️ Fix Ticker for {stock.display_name}</p>
                          <div className="flex gap-3 items-end">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Ticker Symbol</label>
                              <input
                                type="text"
                                placeholder="e.g. RELIANCE"
                                value={tickerInputs[stock.display_name] || ''}
                                onChange={(e) => handleTickerInputChange(stock.display_name, e.target.value)}
                                className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400 uppercase w-40"
                              />
                            </div>
                            <Button size="sm" onClick={() => fixTickerMapping(stock.display_name)} disabled={savingAction === stock.display_name || !tickerInputs[stock.display_name]} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                              {savingAction === stock.display_name ? <RefreshCw size={14} className="animate-spin" /> : 'Save Ticker'}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                      {/* Update Price Form */}
                      {editFormData[stock.display_name]?.action === 'update_price' && (
                        <div className="space-y-3">
                          <p className="font-medium text-emerald-800 text-sm">💰 Update Manual Price for {stock.display_name}</p>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">New Manual Price (₹)</label>
                              <input type="number" placeholder={stock.manual_price ? stock.manual_price.toString() : "e.g. 150.00"} value={editFormData[stock.display_name]?.price || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'price', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400" min="0" step="0.01" />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs text-gray-600 mb-1">Notes</label>
                              <input type="text" placeholder="Source of valuation, date..." value={editFormData[stock.display_name]?.notes || ''} onChange={(e) => handleEditFormChange(stock.display_name, 'notes', e.target.value)} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:border-emerald-400" />
                            </div>
                            <div className="flex items-end gap-2">
                              <Button size="sm" onClick={() => markAsPrivate(stock.display_name)} disabled={savingAction === stock.display_name} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                {savingAction === stock.display_name ? <RefreshCw size={14} className="animate-spin" /> : 'Update'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setExpandedEditRow(null)}>Cancel</Button>
                            </div>
                          </div>
                          {stock.manual_price && (
                            <p className="text-xs text-gray-500">Current price: ₹{stock.manual_price.toLocaleString()}</p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {filteredCompanies.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Building2 size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No private stocks found</p>
              <p className="text-sm mt-1">Mark stocks as private and set manual prices from the Imports → Symbols page</p>
            </div>
          )}
        </div>
        )}

        {/* Pledge Status Tab */}
        {activeTab === 'pledge' && (
        <div className="space-y-4">
          {/* Pledge Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* LAS Pledged Total */}
            <div className="card bg-gradient-to-br from-red-50 to-orange-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🔒</span>
                <h3 className="font-bold text-red-800">LAS Pledged</h3>
              </div>
              <p className="text-2xl font-bold text-red-700">
                {formatValue(pledgeData.las?.total_summary?.total_value || 0)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {(pledgeData.las?.total_summary?.total_qty || 0).toLocaleString()} shares • {pledgeData.las?.banks?.length || 0} banks
              </p>
            </div>
            
            {/* MTF */}
            <div className="card bg-gradient-to-br from-amber-50 to-yellow-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">📈</span>
                <h3 className="font-bold text-amber-800">MTF Holdings</h3>
              </div>
              <p className="text-2xl font-bold text-amber-700">
                {formatValue(pledgeData.mtf?.summary?.total_value || 0)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {(pledgeData.mtf?.summary?.total_qty || 0).toLocaleString()} shares • {pledgeData.mtf?.stocks?.length || 0} stocks
              </p>
            </div>
            
            {/* Free */}
            <div className="card bg-gradient-to-br from-green-50 to-emerald-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">✅</span>
                <h3 className="font-bold text-green-800">Free (Unpledged)</h3>
              </div>
              <p className="text-2xl font-bold text-green-700">
                {formatValue(pledgeData.free?.summary?.total_value || 0)}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {(pledgeData.free?.summary?.total_qty || 0).toLocaleString()} shares • {pledgeData.free?.stocks?.length || 0} stocks
              </p>
            </div>
          </div>

          {/* LAS by Bank Tables */}
          {pledgeData.las?.banks?.map(bank => (
          <div key={bank} className="card overflow-x-auto">
            <div className="px-4 py-3 bg-red-50 border-b border-red-100">
              <h3 className="font-bold text-red-800 flex items-center gap-2">
                🏦 {bank} LAS ({pledgeData.las?.by_bank?.[bank]?.stocks?.length || 0} stocks)
                <span className="ml-auto text-sm font-normal">
                  {formatValue(pledgeData.las?.by_bank?.[bank]?.summary?.total_value || 0)}
                </span>
              </h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b bg-red-50/50">
                  <th className="text-left py-2 px-3 font-semibold text-sm">Stock</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Pledged Qty</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Value</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Gain</th>
                  <th className="text-left py-2 px-3 font-semibold text-sm">Members</th>
                </tr>
              </thead>
              <tbody>
                {(pledgeData.las?.by_bank?.[bank]?.stocks || []).map((stock, idx) => (
                  <tr key={idx} className="border-b hover:bg-red-50/30">
                    <td className="py-2 px-3">
                      <div className="font-medium">{stock.display_name || stock.symbol}</div>
                      <div className="text-xs text-gray-500">{stock.symbol}</div>
                    </td>
                    <td className="text-right py-2 px-3 font-medium">{stock.quantity?.toLocaleString()}</td>
                    <td className="text-right py-2 px-3 font-medium text-red-700">{formatValue(stock.current_value || 0)}</td>
                    <td className={`text-right py-2 px-3 font-medium ${stock.gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatValue(stock.gain || 0)}
                      <span className="text-xs ml-1">({stock.gain_percent || 0}%)</span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        {stock.members?.slice(0,2).map((m, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{m}</span>
                        ))}
                        {stock.members?.length > 2 && <span className="text-xs text-gray-400">+{stock.members.length - 2}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          ))}

          {/* MTF Table */}
          {pledgeData.mtf?.stocks?.length > 0 && (
          <div className="card overflow-x-auto">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
              <h3 className="font-bold text-amber-800 flex items-center gap-2">
                📈 MTF Holdings ({pledgeData.mtf?.stocks?.length || 0} stocks)
                <span className="ml-auto text-sm font-normal">
                  {formatValue(pledgeData.mtf?.summary?.total_value || 0)}
                </span>
              </h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b bg-amber-50/50">
                  <th className="text-left py-2 px-3 font-semibold text-sm">Stock</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Qty</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Value</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Gain</th>
                  <th className="text-left py-2 px-3 font-semibold text-sm">Members</th>
                </tr>
              </thead>
              <tbody>
                {pledgeData.mtf.stocks.map((stock, idx) => (
                  <tr key={idx} className="border-b hover:bg-amber-50/30">
                    <td className="py-2 px-3">
                      <div className="font-medium">{stock.display_name || stock.symbol}</div>
                      <div className="text-xs text-gray-500">{stock.symbol}</div>
                    </td>
                    <td className="text-right py-2 px-3 font-medium">{stock.quantity?.toLocaleString()}</td>
                    <td className="text-right py-2 px-3 font-medium text-amber-700">{formatValue(stock.current_value || 0)}</td>
                    <td className={`text-right py-2 px-3 font-medium ${stock.gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatValue(stock.gain || 0)}
                      <span className="text-xs ml-1">({stock.gain_percent || 0}%)</span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        {stock.members?.slice(0,2).map((m, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{m}</span>
                        ))}
                        {stock.members?.length > 2 && <span className="text-xs text-gray-400">+{stock.members.length - 2}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          
          {pledgeData.mtf?.stocks?.length === 0 && (
            <div className="card p-4 bg-amber-50/50 border border-amber-200">
              <p className="text-amber-800 text-sm">📈 No MTF holdings found. MTF data shows in the pledge_details field with &quot;MS&quot; account code.</p>
            </div>
          )}

          {/* Free Shares Table */}
          <div className="card overflow-x-auto">
            <div className="px-4 py-3 bg-green-50 border-b border-green-100">
              <h3 className="font-bold text-green-800 flex items-center gap-2">
                ✅ Free (Unpledged) Shares ({pledgeData.free?.stocks?.length || 0} stocks)
                <span className="ml-auto text-sm font-normal">
                  {formatValue(pledgeData.free?.summary?.total_value || 0)}
                </span>
              </h3>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b bg-green-50/50">
                  <th className="text-left py-2 px-3 font-semibold text-sm">Stock</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Qty</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Value</th>
                  <th className="text-right py-2 px-3 font-semibold text-sm">Gain</th>
                  <th className="text-left py-2 px-3 font-semibold text-sm">Members</th>
                </tr>
              </thead>
              <tbody>
                {(pledgeData.free?.stocks || []).slice(0, 50).map((stock, idx) => (
                  <tr key={idx} className="border-b hover:bg-green-50/30">
                    <td className="py-2 px-3">
                      <div className="font-medium">{stock.display_name || stock.symbol}</div>
                      <div className="text-xs text-gray-500">{stock.symbol}</div>
                    </td>
                    <td className="text-right py-2 px-3 font-medium">{stock.quantity?.toLocaleString()}</td>
                    <td className="text-right py-2 px-3 font-medium text-green-700">{formatValue(stock.current_value || 0)}</td>
                    <td className={`text-right py-2 px-3 font-medium ${stock.gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatValue(stock.gain || 0)}
                      <span className="text-xs ml-1">({stock.gain_percent || 0}%)</span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        {stock.members?.slice(0,2).map((m, i) => (
                          <span key={i} className="px-2 py-0.5 bg-gray-100 rounded text-xs">{m}</span>
                        ))}
                        {stock.members?.length > 2 && <span className="text-xs text-gray-400">+{stock.members.length - 2}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pledgeData.free?.stocks?.length > 50 && (
              <div className="text-center py-2 text-sm text-gray-500 border-t">
                Showing 50 of {pledgeData.free.stocks.length} free stocks
              </div>
            )}
            {(!pledgeData.free?.stocks || pledgeData.free.stocks.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                <p>No free shares found</p>
              </div>
            )}
          </div>
        </div>
        )}

        {/* All Holdings Tab - Individual holdings view (uses reusable AllHoldingsView component) */}
        {activeTab === 'holdings' && (
          <AllHoldingsView holdings={allHoldings} members={members} onRefresh={fetchCompanies} />
        )}

        {/* Stock Analysis Modal - Extracted Component */}
        <StockAnalysisModal
          isOpen={showAnalysisModal}
          onClose={setShowAnalysisModal}
          selectedStock={selectedStock}
          stockAnalysis={stockAnalysis}
          aiAnalysis={aiAnalysis}
          loadingAnalysis={loadingAnalysis}
          loadingAI={loadingAI}
          onGetAIAnalysis={handleGetAIAnalysis}
        />

        {/* Holdings Detail Modal - Extracted Component */}
        <HoldingsDetailModal
          isOpen={showHoldingsModal}
          onClose={setShowHoldingsModal}
          company={selectedCompanyForHoldings}
          holdings={holdingsDetails}
          loading={loadingHoldings}
          activeTab={activeTab}
        />

        {/* Notes Viewer/Editor Modal */}
        <Dialog open={showNotesModal} onOpenChange={(open) => {
          setShowNotesModal(open);
          if (!open) {
            setIsEditingNotes(false);
            setEditingNotesText('');
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="text-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📝</span>
                  <span>{viewingNotesStock?.display_name || viewingNotesStock?.symbol || 'Notes'}</span>
                </div>
                {!isEditingNotes && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const currentNotes = stockNotes[viewingNotesStock?.display_name || viewingNotesStock?.symbol] || 
                                          viewingNotesStock?.notes || viewingNotesStock?.merger_notes || '';
                      setEditingNotesText(currentNotes);
                      setIsEditingNotes(true);
                    }}
                    className="text-xs gap-1"
                  >
                    ✏️ Edit
                  </Button>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {viewingNotesStock && (
              <div className="flex-1 overflow-y-auto">
                {isEditingNotes ? (
                  /* Edit Mode - Rich Text Editor */
                  <div className="space-y-4">
                    {/* Quick Format Hints */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-700 font-medium mb-1">💡 Format Tips:</p>
                      <p className="text-xs text-blue-600">
                        Start a line with <code className="bg-blue-100 px-1 rounded">STATUS:</code> or <code className="bg-blue-100 px-1 rounded">ACTION:</code> for special formatting
                      </p>
                    </div>
                    
                    {/* Large Textarea */}
                    <textarea
                      value={editingNotesText}
                      onChange={(e) => setEditingNotesText(e.target.value)}
                      placeholder="Enter notes here...

Example:
STATUS: Active Trading. Strong performer.
ACTION: High Value. Consider holding.

Or just write free-form notes..."
                      className="w-full h-64 p-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none font-mono"
                      autoFocus
                    />
                    
                    {/* Action Buttons */}
                    <div className="flex justify-between items-center pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setIsEditingNotes(false);
                          setEditingNotesText('');
                        }}
                        className="text-xs"
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={async () => {
                          setSavingNotes(true);
                          const stockKey = viewingNotesStock?.display_name || viewingNotesStock?.symbol;
                          try {
                            await saveNotes(stockKey, editingNotesText);
                            setStockNotes(prev => ({ ...prev, [stockKey]: editingNotesText }));
                            setIsEditingNotes(false);
                            toast.success('Notes saved successfully');
                          } catch (err) {
                            toast.error('Failed to save notes');
                          } finally {
                            setSavingNotes(false);
                          }
                        }}
                        disabled={savingNotes}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1"
                      >
                        {savingNotes ? (
                          <>
                            <RefreshCw size={12} className="animate-spin" />
                            Saving...
                          </>
                        ) : (
                          '💾 Save Notes'
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* View Mode - Formatted Display */
                  <div className="space-y-4">
                    {/* Stock Info Header */}
                    <div className="flex items-center gap-3 pb-3 border-b">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-md">
                        {(viewingNotesStock.display_name || viewingNotesStock.symbol || '?').charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-lg">{viewingNotesStock.display_name || viewingNotesStock.symbol}</p>
                        {viewingNotesStock.ticker && (
                          <p className="text-sm text-gray-500">Ticker: {viewingNotesStock.ticker}</p>
                        )}
                      </div>
                    </div>

                    {/* Notes Content */}
                    {(stockNotes[viewingNotesStock.display_name || viewingNotesStock.symbol] || viewingNotesStock.notes || viewingNotesStock.merger_notes) ? (
                      <div className="space-y-4">
                        {(() => {
                          const notes = stockNotes[viewingNotesStock.display_name || viewingNotesStock.symbol] || viewingNotesStock.notes || viewingNotesStock.merger_notes || '';
                          const lines = notes.split('\n');
                          
                          return lines.map((line, idx) => {
                            if (line.startsWith('STATUS:')) {
                              return (
                                <div key={idx} className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">📊</span>
                                    <p className="text-sm font-bold text-amber-800 uppercase tracking-wide">Status</p>
                                  </div>
                                  <p className="text-base text-amber-900 leading-relaxed">
                                    {line.replace('STATUS:', '').trim()}
                                  </p>
                                </div>
                              );
                            } else if (line.startsWith('ACTION:')) {
                              return (
                                <div key={idx} className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 shadow-sm">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="text-lg">⚡</span>
                                    <p className="text-sm font-bold text-emerald-800 uppercase tracking-wide">Recommended Action</p>
                                  </div>
                                  <p className="text-base text-emerald-900 leading-relaxed">
                                    {line.replace('ACTION:', '').trim()}
                                  </p>
                                </div>
                              );
                            } else if (line.trim()) {
                              return (
                                <p key={idx} className="text-base text-gray-700 leading-relaxed px-1">{line}</p>
                              );
                            }
                            return null;
                          });
                        })()}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <span className="text-5xl mb-3 block">📭</span>
                        <p className="text-lg font-medium">No notes yet</p>
                        <p className="text-sm mt-1">Click Edit to add notes for this stock</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Transaction Dialog */}
        <AddTransactionDialog
          isOpen={showTransactionDialog}
          onClose={() => {
            setShowTransactionDialog(false);
            setTransactionSymbol('');
            // Clear highlight after a delay to allow visual feedback
            setTimeout(() => setHighlightedSymbol(null), 3000);
          }}
          onSuccess={() => {
            setShowTransactionDialog(false);
            setTransactionSymbol('');
            fetchCompanies(); // Refresh data after transaction
            // Clear highlight after a delay
            setTimeout(() => setHighlightedSymbol(null), 3000);
          }}
          onSymbolChange={(symbol) => {
            // Set highlighted symbol when user selects a different stock in the dialog
            setHighlightedSymbol(symbol);
          }}
          showTrigger={false}
          defaultSymbol={transactionSymbol}
        />
      </div>
    </Layout>
  );
};

export default Companies;
